import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, BookOpen, Award, DollarSign, Clock, 
  TrendingUp, FileText, LogOut, ChevronRight,
  Plus, Download, Search, X, CheckCircle, XCircle,
  Building2, Heart, Mail, Phone, MapPin, Trash2, CreditCard,
  Key, Ban, ShieldCheck
} from 'lucide-react';
import AgreementsPanel from '../components/AgreementsPanel';
import ClinicalFormsPanel from '../components/ClinicalFormsPanel';
import MarketingPostersPanel from '../components/MarketingPostersPanel';
import StudentTranscriptsPanel from '../components/StudentTranscriptsPanel';
import DoctoralRegistryPanel from '../components/DoctoralRegistryPanel';
import CMARenewalsPanel from '../components/CMARenewalsPanel';
import StudentProgressPanel from '../components/StudentProgressPanel';
import api from '../api';


// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

const AdminDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [hoursLogs, setHoursLogs] = useState([]);
  const [pendingHours, setPendingHours] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [modules, setModules] = useState([]);
  const [onlineCourses, setOnlineCourses] = useState({ enrollments: [], stats: {}, course_stats: {} });
  const [onlineCertificates, setOnlineCertificates] = useState({ total_certificates: 0, certificates: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [programFilter, setProgramFilter] = useState('all');

  // Selected student for the progress panel modal
  const [progressUserId, setProgressUserId] = useState(null);

  // Modal states
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [showManualEnrollModal, setShowManualEnrollModal] = useState(false);
  const [availableOnlineCourses, setAvailableOnlineCourses] = useState([]);
  const [manualEnrollForm, setManualEnrollForm] = useState({
    student_id: '',
    course_id: '',
    waive_payment: true
  });
  const [editingStudent, setEditingStudent] = useState(null);
  const [editStudentForm, setEditStudentForm] = useState({
    apprentice_id: '',
    completion_date: ''
  });

  // Account management modals (password reset, suspend, reinstate)
  const [accountModal, setAccountModal] = useState(null); // { mode: 'reset'|'suspend'|'reinstate', student }
  const [accountForm, setAccountForm] = useState({ new_password: '', reason: '', status: 'suspended', note: '' });

  // Form states
  const [hoursForm, setHoursForm] = useState({
    student_id: '',
    enrollment_id: '',
    date: new Date().toISOString().split('T')[0],
    hours: '',
    activity_type: 'classroom',
    description: ''
  });

  const [transactionForm, setTransactionForm] = useState({
    student_id: '',
    enrollment_id: '',
    transaction_type: 'tuition_payment',
    category: 'student_payment',
    amount: '',
    description: '',
    payment_method: 'cash',
    reference_number: ''
  });

  // Financial summary
  const [financialSummary, setFinancialSummary] = useState({
    totalRevenue: 0,
    totalGrantsReceived: 0,
    totalExpenses: 0,
    netIncome: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, studentsRes, enrollmentsRes, transactionsRes, hoursRes, pendingRes, employersRes, partnersRes, modulesRes, onlineCoursesRes, onlineCertsRes] = await Promise.all([
        api.get('/stats/dashboard'),
        api.get('/students'),
        api.get('/enrollments'),
        api.get('/admin/transactions').catch(() => ({ data: { transactions: [] } })),
        api.get('/admin/hours-logs').catch(() => ({ data: { logs: [] } })),
        api.get('/admin/hours/pending').catch(() => ({ data: { logs: [] } })),
        api.get('/admin/employers').catch(() => ({ data: { employers: [] } })),
        api.get('/admin/partners').catch(() => ({ data: { partners: [] } })),
        api.get('/modules').catch(() => ({ data: [] })),
        api.get('/admin/online-courses/enrollments').catch(() => ({ data: { enrollments: [], stats: {}, course_stats: {} } })),
        api.get('/admin/online-courses/certificates').catch(() => ({ data: { total_certificates: 0, certificates: [] } }))
      ]);
      
      setStats(statsRes.data);
      setStudents(studentsRes.data);
      setEnrollments(enrollmentsRes.data);
      setTransactions(transactionsRes.data.transactions || []);
      setHoursLogs(hoursRes.data.logs || []);
      setPendingHours(pendingRes.data.logs || []);
      setEmployers(employersRes.data.employers || []);
      setPartners(partnersRes.data.partners || []);
      setModules(modulesRes.data || []);
      setOnlineCourses(onlineCoursesRes.data || { enrollments: [], stats: {}, course_stats: {} });
      setOnlineCertificates(onlineCertsRes.data || { total_certificates: 0, certificates: [] });
      
      // Calculate financial summary
      const txns = transactionsRes.data.transactions || [];
      const revenue = txns.filter(t => t.transaction_type === 'revenue' || t.transaction_type === 'tuition_payment')
        .reduce((sum, t) => sum + t.amount, 0);
      const grants = txns.filter(t => t.transaction_type === 'grant_received')
        .reduce((sum, t) => sum + t.amount, 0);
      const expenses = txns.filter(t => t.transaction_type === 'expense')
        .reduce((sum, t) => sum + t.amount, 0);
      
      setFinancialSummary({
        totalRevenue: revenue,
        totalGrantsReceived: grants,
        totalExpenses: expenses,
        netIncome: revenue + grants - expenses
      });
      
      // Fetch available online courses for manual enrollment
      const coursesRes = await api.get('/online-courses').catch(() => ({ data: [] }));
      setAvailableOnlineCourses(coursesRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (studentId, program) => {
    try {
      const startDate = new Date();
      await api.post('/enrollments', {
        student_id: studentId,
        program_code: program || 'CNA',
        program_start_date: startDate.toISOString()
      });
      alert('Student enrolled successfully!');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to enroll student');
    }
  };

  const handleManualOnlineEnroll = async (e) => {
    e.preventDefault();
    try {
      const selectedStudent = students.find(s => s.id === manualEnrollForm.student_id);
      if (!selectedStudent) {
        alert('Please select a student');
        return;
      }
      
      await api.post('/admin/online-courses/enrollment/manual', null, {
        params: {
          user_id: selectedStudent.id,
          user_email: selectedStudent.email,
          user_name: selectedStudent.full_name,
          course_id: manualEnrollForm.course_id,
          waive_payment: manualEnrollForm.waive_payment
        }
      });
      
      alert(`Student enrolled in online course successfully!${manualEnrollForm.waive_payment ? ' (Payment waived)' : ''}`);
      setShowManualEnrollModal(false);
      setManualEnrollForm({
        student_id: '',
        course_id: '',
        waive_payment: true
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to enroll student in online course');
    }
  };

  const handleForceComplete = async (enrollmentId) => {
    if (!window.confirm('Force complete this enrollment for testing?\n\nThis will mark all modules as complete and generate a certificate.')) {
      return;
    }
    
    try {
      const response = await api.post(`/admin/online-courses/enrollment/${enrollmentId}/force-complete`);
      alert(`✅ ${response.data.message}\n\nStudent: ${response.data.student_name}\nCourse: ${response.data.course_title}\nVerification Code: ${response.data.verification_code}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to force complete enrollment');
    }
  };

  const handleLogHours = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/hours-log', {
        ...hoursForm,
        hours: parseFloat(hoursForm.hours),
        date: new Date(hoursForm.date).toISOString()
      });
      alert('Hours logged successfully!');
      setShowHoursModal(false);
      setHoursForm({
        student_id: '',
        enrollment_id: '',
        date: new Date().toISOString().split('T')[0],
        hours: '',
        activity_type: 'classroom',
        description: ''
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to log hours');
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/transactions', {
        ...transactionForm,
        amount: parseFloat(transactionForm.amount),
        student_id: transactionForm.student_id || null,
        enrollment_id: transactionForm.enrollment_id || null
      });
      alert('Transaction recorded successfully!');
      setShowTransactionModal(false);
      setTransactionForm({
        student_id: '',
        enrollment_id: '',
        transaction_type: 'tuition_payment',
        category: 'student_payment',
        amount: '',
        description: '',
        payment_method: 'cash',
        reference_number: ''
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to record transaction');
    }
  };

  const handleDownloadReport = async (reportType, filename) => {
    try {
      const response = await api.get(`/reports/${reportType}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to download ${reportType} report`);
    }
  };

  const handleApproveHours = async (logId) => {
    try {
      await api.put(`/admin/hours/${logId}/approve`);
      alert('Hours approved successfully!');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve hours');
    }
  };

  const handleRejectHours = async (logId) => {
    if (!window.confirm('Are you sure you want to reject these hours?')) return;
    try {
      await api.put(`/admin/hours/${logId}/reject`);
      alert('Hours rejected.');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject hours');
    }
  };

  const handleGenerateCertificate = async (studentId) => {
    try {
      const response = await api.post(`/certificates/${studentId}/generate`);
      alert(`Certificate generated! Number: ${response.data.certificate_number}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate certificate');
    }
  };

  const handleDownloadCertificate = async (studentId, studentName) => {
    try {
      const response = await api.get(`/certificates/${studentId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificate_${studentName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 404) {
        alert('Certificate not found. Please generate the certificate first by clicking "Generate".');
      } else {
        alert(err.response?.data?.detail || 'Failed to download certificate');
      }
    }
  };

  // Edit student apprentice ID and certificate date
  const handleEditStudent = (student) => {
    setEditingStudent(student);
    setEditStudentForm({
      apprentice_id: student.apprentice_id || '',
      completion_date: ''
    });
    setShowEditStudentModal(true);
  };

  const handleUpdateApprenticeId = async () => {
    if (!editingStudent) return;
    try {
      await api.put(`/admin/students/${editingStudent.id}/apprentice-id`, {
        apprentice_id: editStudentForm.apprentice_id
      });
      alert('Apprentice ID updated successfully');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update Apprentice ID');
    }
  };

  const handleUpdateCompletionDate = async () => {
    if (!editingStudent || !editStudentForm.completion_date) {
      alert('Please select a completion date');
      return;
    }
    try {
      await api.put(`/admin/certificates/${editingStudent.id}/completion-date`, {
        completion_date: editStudentForm.completion_date
      });
      alert('Certificate completion date updated successfully');
      setShowEditStudentModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update completion date');
    }
  };

  // Delete handlers
  const handleDeleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to delete student "${studentName}" and all their records? This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/admin/students/${studentId}`);
      alert('Student deleted successfully');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete student');
    }
  };

  // Account management: open modal pre-loaded for the selected student
  const openAccountModal = (mode, student) => {
    setAccountModal({ mode, student });
    setAccountForm({
      new_password: '',
      reason: '',
      status: 'suspended',
      note: '',
    });
  };

  const closeAccountModal = () => {
    setAccountModal(null);
    setAccountForm({ new_password: '', reason: '', status: 'suspended', note: '' });
  };

  const submitAccountAction = async () => {
    if (!accountModal) return;
    const { mode, student } = accountModal;
    try {
      if (mode === 'reset') {
        if (!accountForm.new_password || accountForm.new_password.length < 6) {
          alert('Password must be at least 6 characters.');
          return;
        }
        await api.post(`/admin/users/${student.id}/reset-password`, {
          new_password: accountForm.new_password,
        });
        alert(`Password updated for ${student.full_name}. They can now sign in with the new password.`);
      } else if (mode === 'suspend') {
        if (!accountForm.reason || accountForm.reason.trim().length < 3) {
          alert('Please provide a reason for the suspension/block.');
          return;
        }
        await api.post(`/admin/users/${student.id}/suspend`, {
          reason: accountForm.reason.trim(),
          status: accountForm.status,
        });
        alert(`${student.full_name} has been ${accountForm.status}.`);
      } else if (mode === 'reinstate') {
        await api.post(`/admin/users/${student.id}/reinstate`, {
          note: accountForm.note || '',
        });
        alert(`${student.full_name} has been reinstated.`);
      }
      closeAccountModal();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Action failed. Please try again.');
    }
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    if (!window.confirm('Are you sure you want to delete this enrollment? This cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/admin/enrollments/${enrollmentId}`);
      alert('Enrollment deleted successfully');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete enrollment');
    }
  };

  const handleDeleteHoursLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this hours log? This cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/admin/hours/${logId}`);
      alert('Hours log deleted successfully');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete hours log');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction? This cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/admin/transactions/${transactionId}`);
      alert('Transaction deleted successfully');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete transaction');
    }
  };

  const handleUpdateEmployerStatus = async (employerId, newStatus) => {
    try {
      await api.put(`/admin/employers/${employerId}/status?status=${newStatus}`);
      alert(`Employer status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleUpdatePartnerStatus = async (partnerId, newStatus) => {
    try {
      await api.put(`/admin/partners/${partnerId}/status?status=${newStatus}`);
      alert(`Partner status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleStudentSelect = (studentId) => {
    const enrollment = enrollments.find(e => e.student_id === studentId && e.status === 'active');
    setHoursForm(prev => ({
      ...prev,
      student_id: studentId,
      enrollment_id: enrollment?.id || ''
    }));
  };

  const handleTransactionStudentSelect = (studentId) => {
    const enrollment = enrollments.find(e => e.student_id === studentId && e.status === 'active');
    setTransactionForm(prev => ({
      ...prev,
      student_id: studentId,
      enrollment_id: enrollment?.id || ''
    }));
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProgram = programFilter === 'all' || student.program === programFilter;
    return matchesSearch && matchesProgram;
  });

  const enrolledStudents = students.filter(s => 
    enrollments.some(e => e.student_id === s.id && e.status === 'active')
  );

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard" data-testid="admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginTop: '4px' }}>
            MedCare Training Academy Management
          </p>
        </div>
        <div className="user-info">
          <a
            href="https://www.facebook.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-facebook-link"
            title="Open MedCare Facebook page"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 8,
              background: '#1877f2',
              color: '#ffffff',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '0.82rem',
              marginRight: 8,
            }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
              <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.77l-.44 2.91h-2.33V22c4.78-.76 8.43-4.92 8.43-9.94z"/>
            </svg>
            Facebook
          </a>
          <span data-testid="admin-name">{user.full_name}</span>
          <button onClick={onLogout} className="btn-logout" data-testid="logout-btn">
            <LogOut size={16} style={{ marginRight: '6px' }} />
            Logout
          </button>
        </div>
      </div>
      
      <div className="dashboard-content">
        {/* Stats Overview */}
        <div className="stats-grid" data-testid="stats-grid">
          <div className="stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div className="stat-value" data-testid="total-students">{stats?.total_students || 0}</div>
                <div className="stat-label">Total Students</div>
              </div>
              <Users size={32} style={{ color: 'var(--gray-300)' }} />
            </div>
          </div>
          <div className="stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div className="stat-value" data-testid="active-enrollments">{stats?.active_enrollments || 0}</div>
                <div className="stat-label">Active Enrollments</div>
              </div>
              <BookOpen size={32} style={{ color: 'var(--gray-300)' }} />
            </div>
          </div>
          <div className="stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div className="stat-value" data-testid="certificates-issued">{stats?.certificates_issued || 0}</div>
                <div className="stat-label">Certificates Issued</div>
              </div>
              <Award size={32} style={{ color: 'var(--gray-300)' }} />
            </div>
          </div>
          <div className="stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div className="stat-value" style={{ color: 'var(--success)' }}>
                  ${financialSummary.netIncome.toLocaleString()}
                </div>
                <div className="stat-label">Net Revenue</div>
              </div>
              <TrendingUp size={32} style={{ color: 'var(--gray-300)' }} />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="dashboard-tabs">
          <button 
            className={`dashboard-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            data-testid="overview-tab"
          >
            Overview
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
            data-testid="students-tab"
          >
            Students
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
            data-testid="pending-tab"
            style={{ position: 'relative' }}
          >
            Pending Approvals
            {pendingHours.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: 'var(--error)',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: '18px',
                textAlign: 'center'
              }}>{pendingHours.length}</span>
            )}
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'hours' ? 'active' : ''}`}
            onClick={() => setActiveTab('hours')}
            data-testid="hours-tab"
          >
            Hours Logged
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'accounting' ? 'active' : ''}`}
            onClick={() => setActiveTab('accounting')}
            data-testid="accounting-tab"
          >
            Accounting
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
            data-testid="payments-tab"
            style={{ background: activeTab === 'payments' ? '#22c55e' : undefined, color: activeTab === 'payments' ? 'white' : undefined }}
          >
            💳 Process Payment
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'certificates' ? 'active' : ''}`}
            onClick={() => setActiveTab('certificates')}
            data-testid="certificates-tab"
          >
            Certificates
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'ged-credentials' ? 'active' : ''}`}
            onClick={() => setActiveTab('ged-credentials')}
            data-testid="ged-credentials-tab"
            style={{ 
              background: activeTab === 'ged-credentials' ? '#7c3aed' : undefined, 
              color: activeTab === 'ged-credentials' ? 'white' : undefined 
            }}
          >
            GED Credentials
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'online-courses' ? 'active' : ''}`}
            onClick={() => setActiveTab('online-courses')}
            data-testid="online-courses-tab"
            style={{ 
              background: activeTab === 'online-courses' ? '#059669' : undefined, 
              color: activeTab === 'online-courses' ? 'white' : undefined,
              position: 'relative'
            }}
          >
            🎓 Online Courses
            {onlineCourses.stats?.in_progress > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: '#22c55e',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: '18px',
                textAlign: 'center'
              }}>{onlineCourses.stats.in_progress}</span>
            )}
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'agreements' ? 'active' : ''}`}
            onClick={() => setActiveTab('agreements')}
            data-testid="agreements-tab"
            style={{ 
              background: activeTab === 'agreements' ? '#0b2545' : undefined, 
              color: activeTab === 'agreements' ? 'white' : undefined 
            }}
          >
            671 Contracts
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'clinical-forms' ? 'active' : ''}`}
            onClick={() => setActiveTab('clinical-forms')}
            data-testid="clinical-forms-tab"
            style={{ 
              background: activeTab === 'clinical-forms' ? '#0d9488' : undefined, 
              color: activeTab === 'clinical-forms' ? 'white' : undefined 
            }}
          >
            Clinical Forms
          </button>
          <button
            className={`dashboard-tab ${activeTab === 'posters' ? 'active' : ''}`}
            onClick={() => setActiveTab('posters')}
            data-testid="posters-tab"
            style={{
              background: activeTab === 'posters' ? '#b45309' : undefined,
              color: activeTab === 'posters' ? 'white' : undefined,
            }}
          >
            Marketing Posters
          </button>
          <button
            className={`dashboard-tab ${activeTab === 'transcripts' ? 'active' : ''}`}
            onClick={() => setActiveTab('transcripts')}
            data-testid="transcripts-tab"
            style={{
              background: activeTab === 'transcripts' ? '#0d9488' : undefined,
              color: activeTab === 'transcripts' ? 'white' : undefined,
            }}
          >
            Student Transcripts
          </button>
          <button
            className={`dashboard-tab ${activeTab === 'doctoral-registry' ? 'active' : ''}`}
            onClick={() => setActiveTab('doctoral-registry')}
            data-testid="doctoral-registry-tab"
            style={{
              background: activeTab === 'doctoral-registry' ? '#5c1a1b' : undefined,
              color: activeTab === 'doctoral-registry' ? '#fbf6e8' : undefined,
            }}
          >
            Doctoral Registry
          </button>
          <button
            className={`dashboard-tab ${activeTab === 'cma-renewals' ? 'active' : ''}`}
            onClick={() => setActiveTab('cma-renewals')}
            data-testid="cma-renewals-tab"
            style={{
              background: activeTab === 'cma-renewals' ? '#0d9488' : undefined,
              color: activeTab === 'cma-renewals' ? 'white' : undefined,
            }}
          >
            CMA Renewals
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'modules' ? 'active' : ''}`}
            onClick={() => setActiveTab('modules')}
            data-testid="modules-tab"
          >
            <BookOpen size={14} style={{ marginRight: '4px' }} />
            Training Modules
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'employers' ? 'active' : ''}`}
            onClick={() => setActiveTab('employers')}
            data-testid="employers-tab"
            style={{ position: 'relative' }}
          >
            <Building2 size={14} style={{ marginRight: '4px' }} />
            Employers
            {employers.filter(e => e.status === 'pending').length > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: 'var(--primary-blue)',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: '18px',
                textAlign: 'center'
              }}>{employers.filter(e => e.status === 'pending').length}</span>
            )}
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'partners' ? 'active' : ''}`}
            onClick={() => setActiveTab('partners')}
            data-testid="partners-tab"
            style={{ position: 'relative' }}
          >
            <Heart size={14} style={{ marginRight: '4px' }} />
            Partners
            {partners.filter(p => p.status === 'pending').length > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: '#dc2626',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: '18px',
                textAlign: 'center'
              }}>{partners.filter(p => p.status === 'pending').length}</span>
            )}
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
            data-testid="reports-tab"
          >
            Reports
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Quick Actions */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
              <Link to="/manage-modules" className="btn btn-primary" data-testid="manage-modules-link">
                <BookOpen size={16} style={{ marginRight: '8px' }} />
                Manage Modules
              </Link>
              <button className="btn btn-secondary" onClick={() => setActiveTab('students')}>
                <Plus size={16} style={{ marginRight: '8px' }} />
                Enroll Student
              </button>
              <button className="btn btn-secondary" onClick={() => { setActiveTab('hours'); setShowHoursModal(true); }}>
                <Clock size={16} style={{ marginRight: '8px' }} />
                Log Hours
              </button>
              <button 
                className="btn" 
                onClick={() => setActiveTab('online-courses')}
                data-testid="online-courses-quick-btn"
                style={{ background: '#059669', color: 'white' }}
              >
                🎓 Online Courses Management
              </button>
              <Link 
                to="/online-courses" 
                className="btn btn-secondary"
                style={{ textDecoration: 'none', border: '2px solid #059669', color: '#059669' }}
                data-testid="browse-online-courses-link"
              >
                Browse Online Courses
              </Link>
              <a 
                href={`${API_HOST}/api/training-manual/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ textDecoration: 'none' }}
                data-testid="download-manual-admin-btn"
              >
                <FileText size={16} style={{ marginRight: '8px' }} />
                CMA Training Manual
              </a>
              <a 
                href={`${API_HOST}/api/flyer/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ textDecoration: 'none' }}
                data-testid="download-flyer-admin-btn"
              >
                <Download size={16} style={{ marginRight: '8px' }} />
                Marketing Flyer
              </a>
              <Link 
                to="/payroll" 
                className="btn btn-secondary"
                style={{ textDecoration: 'none', background: '#059669', color: 'white' }}
                data-testid="payroll-link"
              >
                <DollarSign size={16} style={{ marginRight: '8px' }} />
                Payroll System
              </Link>
              <Link
                to="/payroll-check"
                className="btn btn-secondary"
                style={{ textDecoration: 'none', background: '#0b2545', color: '#f7d774', fontWeight: 700 }}
                data-testid="payroll-check-printer-link"
                title="Standalone — calculates federal, FICA, state &amp; local taxes; prints check + stub."
              >
                <DollarSign size={16} style={{ marginRight: '8px' }} />
                Payroll Check Printer
              </Link>
            </div>

            {/* Downloadable Resources Card */}
            <div className="card" style={{ marginBottom: '24px', border: '2px solid var(--primary-blue)' }}>
              <div className="card-header" style={{ background: 'var(--primary-blue)', color: 'white' }}>
                <h3 className="card-title" style={{ color: 'white' }}>
                  <FileText size={20} style={{ marginRight: '8px' }} />
                  Downloadable Resources
                </h3>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                  {/* CMA Training Manual - COMPREHENSIVE */}
                  <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#dbeafe' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>CMA Training Manual</h4>
                      <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>COMPREHENSIVE</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#dbeafe', marginBottom: '12px' }}>
                      154-hour college-level study guide with 22 detailed modules, clinical skills checklists, mid-term/final exams, and TN state certification prep.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/cna-training-manual-comprehensive/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#dbeafe', color: '#1e40af', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download PDF
                      </a>
                    </div>
                  </div>
                  
                  {/* PN Tech Training Manual - COMPREHENSIVE */}
                  <div style={{ background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#ccfbf1' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>PN Tech Training Manual</h4>
                      <span style={{ background: '#ccfbf1', color: '#0d9488', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>COMPREHENSIVE</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#ccfbf1', marginBottom: '12px' }}>
                      300-hour college-level Pre-LPN foundational study guide with 22 modules, clinical skills, pharmacology, mid-term/final exams.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/pntech-training-manual-comprehensive/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#ccfbf1', color: '#0d9488', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download PDF
                      </a>
                    </div>
                  </div>
                  
                  {/* Phlebotomy Training Manual */}
                  <div style={{ background: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#fef3c7' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>Phlebotomy Training Manual</h4>
                      <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>COMPREHENSIVE</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#fef3c7', marginBottom: '12px' }}>
                      120-hour college-level study guide with 15 detailed modules, skills checklists, mid-term/final exams, and NHA CPT certification prep.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/phlebotomy-training-manual/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#fef3c7', color: '#b45309', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download PDF
                      </a>
                    </div>
                  </div>
                  
                  {/* EMT Training Manual */}
                  <div style={{ background: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#fecaca' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>EMT Training Manual</h4>
                      <span style={{ background: '#fecaca', color: '#b91c1c', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>COMPREHENSIVE</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#fecaca', marginBottom: '12px' }}>
                      144-hour college-level NREMT preparation with 20 detailed modules, skills procedures, mid-term/final exams, and clinical logs.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/emt-training-manual/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#fecaca', color: '#b91c1c', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download PDF
                      </a>
                    </div>
                  </div>
                  
                  {/* Homeland Security Training Manual */}
                  <div style={{ background: 'linear-gradient(135deg, #1a365d 0%, #3b82f6 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#d4af37' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>Homeland Security Manual</h4>
                      <span style={{ background: '#d4af37', color: '#1a365d', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>COMPREHENSIVE</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#bfdbfe', marginBottom: '12px' }}>
                      154-hour college-level program with 8 detailed units: FEMA ICS, disaster response, TECC, mass casualty management, and capstone simulation.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/homeland-security-training-manual/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#d4af37', color: '#1a365d', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download PDF
                      </a>
                    </div>
                  </div>
                  
                  {/* Marketing Flyer */}
                  <div style={{ background: 'var(--off-white)', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <FileText size={24} style={{ color: 'var(--success)' }} />
                      <h4 style={{ margin: 0 }}>Marketing Flyer</h4>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '12px' }}>
                      Professional CMA program flyer with enrollment info, contact details, and True Victory Ministries sponsor.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/flyer/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                        style={{ textDecoration: 'none' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download PDF
                      </a>
                      <button
                        onClick={() => {
                          const url = `${API_HOST}/api/flyer/download`;
                          window.open(url, '_blank');
                        }}
                        className="btn btn-secondary btn-sm"
                      >
                        <FileText size={14} style={{ marginRight: '4px' }} />
                        View
                      </button>
                    </div>
                  </div>
                  
                  {/* Business Cards */}
                  <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #334155 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <CreditCard size={24} style={{ color: '#fbbf24' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>Business Cards</h4>
                      <span style={{ background: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>NEW</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#e5e7eb', marginBottom: '12px' }}>
                      Printable business cards for all programs: CNA, PN Tech, Phlebotomy, EMT, Homeland Security & Combined.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Link
                        to="/business-cards"
                        className="btn btn-sm"
                        style={{ background: '#fbbf24', color: '#1e3a5f', fontWeight: 'bold', border: 'none', cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        View & Download
                      </Link>
                    </div>
                  </div>
                  
                  {/* Instructor Training Package */}
                  <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#fbbf24' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>Instructor Training Package</h4>
                      <span style={{ background: '#fbbf24', color: '#1e3a5f', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>NEW</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#e5e7eb', marginBottom: '12px' }}>
                      Complete CMA curriculum bundle: Instructor Lecture Guide, Daily Quiz Cards, Midterm &amp; Final Exams.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/training-package/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#fbbf24', color: '#1e3a5f', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download Package
                      </a>
                      <button
                        onClick={() => {
                          const url = `${API_HOST}/api/training-package/download`;
                          window.open(url, '_blank');
                        }}
                        className="btn btn-sm"
                        style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                      >
                        <FileText size={14} style={{ marginRight: '4px' }} />
                        View
                      </button>
                    </div>
                  </div>

                  {/* Combined Healthcare Curriculum - 31-Week Program */}
                  <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #0d9488 100%)', padding: '16px', borderRadius: '8px', color: 'white', border: '2px solid #d4af37' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#d4af37' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>Combined Healthcare Curriculum</h4>
                      <span style={{ background: '#d4af37', color: '#0f172a', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>31-WEEK</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#e5e7eb', marginBottom: '8px' }}>
                      <strong>500 RTI Hours</strong> - Integrated 4-program curriculum combining PNT, EMT, Phlebotomy &amp; Homeland Security.
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#93c5fd', marginBottom: '12px' }}>
                      Schedule: Mon-Thu 10AM-2PM | Weekly curriculum grids | Competency checkpoints | Capstone assessments
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <a 
                        href={`${API_HOST}/api/combined-healthcare-curriculum/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#d4af37', color: '#0f172a', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Curriculum Schedule
                      </a>
                      <a 
                        href={`${API_HOST}/api/combined-healthcare-training-manual/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#22c55e', color: 'white', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Full Training Manual
                      </a>
                    </div>
                  </div>
                  
                  {/* PN Tech Training Package */}
                  <div style={{ background: 'linear-gradient(135deg, #047857 0%, #10b981 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#fef3c7' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>PN Tech Training Package</h4>
                      <span style={{ background: '#fef3c7', color: '#047857', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>NEW</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#d1fae5', marginBottom: '12px' }}>
                      Pre-LPN foundational course materials: Instructor Guide, Quiz Bank, Midterm &amp; Final Exams.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/pn-tech-training-package/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#fef3c7', color: '#047857', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download Package
                      </a>
                      <button
                        onClick={() => {
                          const url = `${API_HOST}/api/pn-tech-training-package/download`;
                          window.open(url, '_blank');
                        }}
                        className="btn btn-sm"
                        style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                      >
                        <FileText size={14} style={{ marginRight: '4px' }} />
                        View
                      </button>
                    </div>
                  </div>
                  
                  {/* Phlebotomy Instructor Package */}
                  <div style={{ background: 'linear-gradient(135deg, #92400e 0%, #f59e0b 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#fef3c7' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>Phlebotomy Instructor Package</h4>
                      <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>NEW</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#fef3c7', marginBottom: '12px' }}>
                      Instructor Guide, Daily Quiz Cards, Skills Checklist, Midterm &amp; Final Exams with Answer Keys.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/phlebotomy-training-package/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#fef3c7', color: '#92400e', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download Package
                      </a>
                    </div>
                  </div>
                  
                  {/* EMT Instructor Package */}
                  <div style={{ background: 'linear-gradient(135deg, #991b1b 0%, #ef4444 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#fecaca' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>EMT Instructor Package</h4>
                      <span style={{ background: '#fecaca', color: '#991b1b', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>NEW</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#fecaca', marginBottom: '12px' }}>
                      NREMT Skills Sheets, Scenario Cards, Quiz Bank, Midterm &amp; Final Exams with Answer Keys.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/emt-training-package/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#fecaca', color: '#991b1b', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download Package
                      </a>
                    </div>
                  </div>
                  
                  {/* Homeland Security Instructor Package */}
                  <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 100%)', padding: '16px', borderRadius: '8px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <BookOpen size={24} style={{ color: '#d4af37' }} />
                      <h4 style={{ margin: 0, color: 'white' }}>Homeland Security Package</h4>
                      <span style={{ background: '#d4af37', color: '#1e3a5f', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>NEW</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#bfdbfe', marginBottom: '12px' }}>
                      ICS/NIMS Materials, Tabletop Exercises, Capstone Simulation Guide, Exams with Keys.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a 
                        href={`${API_HOST}/api/homeland-security-training-package/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#d4af37', color: '#1e3a5f', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Download Package
                      </a>
                    </div>
                  </div>
                </div>

                {/* LPN Training Package */}
                <div style={{ marginTop: '16px', padding: '16px', background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)', borderRadius: '8px', color: 'white' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <h4 style={{ margin: 0, color: 'white' }}>LPN Training Package (9-Month Program)</h4>
                      <p style={{ margin: '4px 0 8px 0', opacity: 0.9, fontSize: '0.875rem' }}>
                        1,500 hours | 19 Modules | Curriculum, Manual, & Quiz Cards
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <a 
                        href={`${API_HOST}/api/lpn-curriculum/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: 'white', color: '#7c3aed', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Curriculum
                      </a>
                      <a 
                        href={`${API_HOST}/api/lpn-training-manual/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: 'white', color: '#7c3aed', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Manual
                      </a>
                      <a 
                        href={`${API_HOST}/api/lpn-quiz-cards/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: 'white', color: '#7c3aed', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Quiz Cards
                      </a>
                      <a 
                        href={`${API_HOST}/api/lpn-training-package/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ textDecoration: 'none', background: '#fbbf24', color: '#7c3aed', fontWeight: 'bold' }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Full Package
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Certificate Samples Card */}
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header">
                <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Award size={20} />
                  Certificate Samples
                </h3>
                <a
                  href={`${API_HOST}/api/certificate-samples/all`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-primary"
                  style={{ textDecoration: 'none' }}
                >
                  <Download size={14} style={{ marginRight: '4px' }} />
                  Download All
                </a>
              </div>
              <div className="card-body">
                <p style={{ marginBottom: '16px', color: 'var(--gray-600)' }}>
                  Preview and download sample certificates for each program. Each certificate features appropriate seals and program-specific information.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                  {/* CMA Certificate */}
                  <a
                    href={`${API_HOST}/api/certificate-preview/CNA`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', padding: '12px', borderRadius: '8px', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
                  >
                    <Award size={24} />
                    <span style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>CMA</span>
                    <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>154 RTI Hours</span>
                  </a>
                  
                  {/* PN Tech Certificate */}
                  <a
                    href={`${API_HOST}/api/certificate-preview/PN_TECH`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)', padding: '12px', borderRadius: '8px', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
                  >
                    <Award size={24} />
                    <span style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>PN Tech</span>
                    <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>300 RTI Hours</span>
                  </a>

                  {/* LPN Certificate */}
                  <a
                    href={`${API_HOST}/api/certificate-preview/LPN`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cert-preview-LPN"
                    style={{ textDecoration: 'none', background: 'linear-gradient(135deg, #047857 0%, #0d9488 100%)', padding: '12px', borderRadius: '8px', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', border: '2px solid #d4af37' }}
                  >
                    <Award size={24} style={{ color: '#d4af37' }} />
                    <span style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>LPN (Hybrid)</span>
                    <span style={{ fontSize: '0.75rem', opacity: 0.85 }}>600 Clinical Hours</span>
                  </a>
                  
                  {/* Phlebotomy Certificate */}
                  <a
                    href={`${API_HOST}/api/certificate-preview/PHLEBOTOMY`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', background: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)', padding: '12px', borderRadius: '8px', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
                  >
                    <Award size={24} />
                    <span style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Phlebotomy</span>
                    <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>120 RTI Hours</span>
                  </a>
                  
                  {/* EMT Certificate */}
                  <a
                    href={`${API_HOST}/api/certificate-preview/EMT`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', padding: '12px', borderRadius: '8px', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
                  >
                    <Award size={24} />
                    <span style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>EMT</span>
                    <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>144 RTI Hours</span>
                  </a>
                  
                  {/* Homeland Security Certificate */}
                  <a
                    href={`${API_HOST}/api/certificate-preview/HOMELAND_SECURITY`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)', padding: '12px', borderRadius: '8px', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', border: '2px solid #d4af37' }}
                  >
                    <Award size={24} style={{ color: '#d4af37' }} />
                    <span style={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#d4af37' }}>Homeland Security</span>
                    <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>154 RTI Hours</span>
                  </a>
                  
                  {/* Combined Healthcare Certificate */}
                  <a
                    href={`${API_HOST}/api/certificate-preview/COMBINED_HEALTHCARE`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', background: 'linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #0d9488 100%)', padding: '12px', borderRadius: '8px', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', border: '2px solid #d4af37' }}
                  >
                    <Award size={24} style={{ color: '#d4af37' }} />
                    <span style={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#d4af37' }}>Combined Program</span>
                    <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>500 RTI Hours</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Recent Enrollments */}
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header">
                <h3 className="card-title">Recent Enrollments</h3>
                <button className="btn btn-sm btn-secondary" onClick={() => setActiveTab('students')}>
                  View All
                </button>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {enrollments.length === 0 ? (
                  <div className="empty-state">
                    <p>No enrollments yet.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Program</th>
                        <th>Start Date</th>
                        <th>Status</th>
                        <th>Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.slice(0, 5).map((enrollment) => {
                        const student = students.find(s => s.id === enrollment.student_id);
                        return (
                          <tr key={enrollment.id}>
                            <td>{student?.full_name || 'Unknown'}</td>
                            <td>{enrollment.program_code || 'CNA'}</td>
                            <td>{new Date(enrollment.program_start_date).toLocaleDateString()}</td>
                            <td>
                              <span className={`status-badge ${enrollment.status}`}>
                                {enrollment.status}
                              </span>
                            </td>
                            <td>{enrollment.hours_logged || 0} / {enrollment.total_hours_required || 75}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Financial Summary */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Financial Summary</h3>
                <button className="btn btn-sm btn-secondary" onClick={() => setActiveTab('accounting')}>
                  View Details
                </button>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
                  <div>
                    <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: '4px' }}>Total Revenue</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success)' }}>
                      ${financialSummary.totalRevenue.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: '4px' }}>Grants Received</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--accent-blue)' }}>
                      ${financialSummary.totalGrantsReceived.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: '4px' }}>Total Expenses</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--error)' }}>
                      ${financialSummary.totalExpenses.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: '4px' }}>Net Income</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', color: financialSummary.netIncome >= 0 ? 'var(--success)' : 'var(--error)' }}>
                      ${financialSummary.netIncome.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div>
            {/* Search and Filter */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input"
                  style={{ paddingLeft: '40px' }}
                  data-testid="search-students"
                />
              </div>
              <select 
                value={programFilter} 
                onChange={(e) => setProgramFilter(e.target.value)}
                className="form-input"
                style={{ width: 'auto' }}
                data-testid="program-filter"
              >
                <option value="all">All Programs</option>
                <option value="CMA">CMA Program</option>
                <option value="PN_TECH">PN Tech Program</option>
                <option value="PHLEBOTOMY">Phlebotomy Program</option>
                <option value="EMT">EMT Program</option>
                <option value="HOMELAND_SECURITY">Homeland Security Program</option>
              </select>
            </div>

            {/* Students Grid */}
            <div className="modules-grid">
              {filteredStudents.length === 0 ? (
                <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                  <div className="empty-state-icon">👥</div>
                  <h3>No students found</h3>
                  <p>Try adjusting your search or filters.</p>
                </div>
              ) : (
                filteredStudents.map((student) => {
                  const enrollment = enrollments.find(e => e.student_id === student.id && e.status === 'active');
                  return (
                    <div key={student.id} className="module-card" data-testid={`student-card-${student.id}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <h3 style={{ marginBottom: '4px' }}>{student.full_name}</h3>
                          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>{student.email}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span className={`status-badge ${enrollment ? 'active' : 'inactive'}`}>
                            {enrollment ? 'Enrolled' : 'Not Enrolled'}
                          </span>
                          {(student.status === 'suspended' || student.status === 'blocked') && (
                            <span
                              data-testid={`student-status-${student.id}`}
                              style={{
                                background: '#fee2e2',
                                color: '#991b1b',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: 12,
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                              }}
                              title={student.suspension_reason || ''}
                            >
                              {student.status}
                            </span>
                          )}
                          <button 
                            onClick={() => handleEditStudent(student)}
                            className="btn btn-sm"
                            style={{ background: 'var(--primary-blue)', color: 'white', padding: '4px 8px' }}
                            title="Edit Student"
                            data-testid={`edit-student-${student.id}`}
                          >
                            <FileText size={14} />
                          </button>
                          <button
                            onClick={() => openAccountModal('reset', student)}
                            className="btn btn-sm"
                            style={{ background: '#0ea5e9', color: 'white', padding: '4px 8px' }}
                            title="Reset Password"
                            data-testid={`reset-password-${student.id}`}
                          >
                            <Key size={14} />
                          </button>
                          {student.status === 'suspended' || student.status === 'blocked' ? (
                            <button
                              onClick={() => openAccountModal('reinstate', student)}
                              className="btn btn-sm"
                              style={{ background: '#16a34a', color: 'white', padding: '4px 8px' }}
                              title="Reinstate Account"
                              data-testid={`reinstate-${student.id}`}
                            >
                              <ShieldCheck size={14} />
                            </button>
                          ) : (
                            <button
                              onClick={() => openAccountModal('suspend', student)}
                              className="btn btn-sm"
                              style={{ background: '#b45309', color: 'white', padding: '4px 8px' }}
                              title="Suspend / Block Student"
                              data-testid={`suspend-${student.id}`}
                            >
                              <Ban size={14} />
                            </button>
                          )}
                          <button 
                            onClick={() => handleDeleteStudent(student.id, student.full_name)}
                            className="btn btn-sm"
                            style={{ background: '#ef4444', color: 'white', padding: '4px 8px' }}
                            title="Delete Student"
                            data-testid={`delete-student-${student.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '8px' }}>
                        Phone: {student.phone}
                      </p>
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '4px' }}>
                        Program: {student.program || 'CNA'}
                      </p>
                      {student.apprentice_id && (
                        <p style={{ fontSize: '0.875rem', color: 'var(--primary-blue)', marginBottom: '16px', fontWeight: '500' }}>
                          Apprentice ID: {student.apprentice_id}
                        </p>
                      )}
                      {!student.apprentice_id && (
                        <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginBottom: '16px', fontStyle: 'italic' }}>
                          No Apprentice ID assigned
                        </p>
                      )}
                      {enrollment ? (
                        <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                          <p>Hours: {enrollment.hours_logged || 0} / {enrollment.total_hours_required || 75}</p>
                          <p>Started: {new Date(enrollment.program_start_date).toLocaleDateString()}</p>
                          <button 
                            onClick={() => handleDeleteEnrollment(enrollment.id)}
                            className="btn btn-sm"
                            style={{ background: '#f97316', color: 'white', marginTop: '8px', fontSize: '0.75rem' }}
                            data-testid={`delete-enrollment-${enrollment.id}`}
                          >
                            <Trash2 size={12} style={{ marginRight: '4px' }} />
                            Remove Enrollment
                          </button>
                        </div>
                      ) : (
                        <button 
                          onClick={() => handleEnroll(student.id, student.program)} 
                          className="btn-module"
                          data-testid={`enroll-btn-${student.id}`}
                        >
                          Enroll Student
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Pending Approvals Tab */}
        {activeTab === 'pending' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, marginBottom: '8px' }}>Pending Hours Approvals</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.9375rem' }}>
                Review and approve student-submitted training hours.
              </p>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                {pendingHours.length === 0 ? (
                  <div className="empty-state" style={{ padding: '48px 24px' }}>
                    <div className="empty-state-icon">✓</div>
                    <h3>All caught up!</h3>
                    <p>No pending hours submissions to review.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Date</th>
                        <th>Hours</th>
                        <th>Activity Type</th>
                        <th>Description</th>
                        <th>Submitted</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingHours.map((log) => {
                        const student = students.find(s => s.id === log.student_id);
                        return (
                          <tr key={log.id} data-testid={`pending-row-${log.id}`}>
                            <td style={{ fontWeight: '500' }}>{student?.full_name || 'Unknown'}</td>
                            <td>{new Date(log.date).toLocaleDateString()}</td>
                            <td><strong>{log.hours}</strong></td>
                            <td style={{ textTransform: 'capitalize' }}>{log.activity_type}</td>
                            <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.description || '-'}
                            </td>
                            <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                              {new Date(log.created_at).toLocaleDateString()}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                <button
                                  onClick={() => handleApproveHours(log.id)}
                                  className="btn btn-success btn-sm"
                                  style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                                  data-testid={`approve-btn-${log.id}`}
                                >
                                  <CheckCircle size={14} style={{ marginRight: '4px' }} />
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectHours(log.id)}
                                  className="btn btn-secondary btn-sm"
                                  style={{ padding: '6px 12px', fontSize: '0.875rem', color: 'var(--error)' }}
                                  data-testid={`reject-btn-${log.id}`}
                                >
                                  <XCircle size={14} style={{ marginRight: '4px' }} />
                                  Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Info Card */}
            <div className="alert alert-info" style={{ marginTop: '24px' }}>
              <p style={{ margin: 0 }}>
                <strong>How it works:</strong> Students can submit their training hours through their portal. 
                Once you approve hours, they are automatically added to the student&apos;s total logged hours.
              </p>
            </div>
          </div>
        )}

        {/* Hours Tab */}
        {activeTab === 'hours' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>Hours Logged</h2>
              <button className="btn btn-primary" onClick={() => setShowHoursModal(true)} data-testid="log-hours-btn">
                <Plus size={16} style={{ marginRight: '8px' }} />
                Log Hours
              </button>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                {hoursLogs.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">⏱️</div>
                    <h3>No hours logged yet</h3>
                    <p>Click &quot;Log Hours&quot; to record student training hours.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Date</th>
                        <th>Hours</th>
                        <th>Activity Type</th>
                        <th>Description</th>
                        <th>Verified By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hoursLogs.map((log) => {
                        const student = students.find(s => s.id === log.student_id);
                        return (
                          <tr key={log.id}>
                            <td>{student?.full_name || 'Unknown'}</td>
                            <td>{new Date(log.date).toLocaleDateString()}</td>
                            <td>{log.hours}</td>
                            <td style={{ textTransform: 'capitalize' }}>{log.activity_type}</td>
                            <td>{log.description || '-'}</td>
                            <td>{log.verified_by || 'Pending'}</td>
                            <td>
                              <button 
                                onClick={() => handleDeleteHoursLog(log.id)}
                                className="btn btn-sm"
                                style={{ background: '#ef4444', color: 'white', padding: '4px 8px' }}
                                title="Delete Hours Log"
                                data-testid={`delete-hours-${log.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Hours Summary by Student */}
            <div className="card" style={{ marginTop: '24px' }}>
              <div className="card-header">
                <h3 className="card-title">Hours Summary by Student</h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Program</th>
                      <th>Hours Logged</th>
                      <th>Hours Required</th>
                      <th>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.filter(e => e.status === 'active').map((enrollment) => {
                      const student = students.find(s => s.id === enrollment.student_id);
                      const progress = ((enrollment.hours_logged || 0) / (enrollment.total_hours_required || 75) * 100).toFixed(0);
                      return (
                        <tr key={enrollment.id}>
                          <td>{student?.full_name || 'Unknown'}</td>
                          <td>{enrollment.program_code || 'CNA'}</td>
                          <td>{enrollment.hours_logged || 0}</td>
                          <td>{enrollment.total_hours_required || 75}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '100px', height: '8px', background: 'var(--gray-200)', borderRadius: '4px', overflow: 'hidden' }}>
                                <div style={{ width: `${Math.min(progress, 100)}%`, height: '100%', background: 'var(--success)', borderRadius: '4px' }} />
                              </div>
                              <span style={{ fontSize: '0.875rem' }}>{progress}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Accounting Tab */}
        {activeTab === 'accounting' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>Financial Management</h2>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button className="btn btn-primary" onClick={() => setShowTransactionModal(true)} data-testid="add-transaction-btn">
                  <Plus size={16} style={{ marginRight: '8px' }} />
                  Add Transaction
                </button>
                <button className="btn btn-secondary">
                  <Download size={16} style={{ marginRight: '8px' }} />
                  Export
                </button>
              </div>
            </div>

            {/* Financial Summary Cards */}
            <div className="stats-grid" style={{ marginBottom: '24px' }}>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--success)' }}>
                <div className="stat-label">Tuition Payments</div>
                <div className="stat-value" style={{ color: 'var(--success)' }}>
                  ${financialSummary.totalRevenue.toLocaleString()}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--accent-blue)' }}>
                <div className="stat-label">Grant Funds Received</div>
                <div className="stat-value" style={{ color: 'var(--accent-blue)' }}>
                  ${financialSummary.totalGrantsReceived.toLocaleString()}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--error)' }}>
                <div className="stat-label">Expenses</div>
                <div className="stat-value" style={{ color: 'var(--error)' }}>
                  ${financialSummary.totalExpenses.toLocaleString()}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--primary-blue)' }}>
                <div className="stat-label">Net Income</div>
                <div className="stat-value">
                  ${financialSummary.netIncome.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Transactions Table */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Recent Transactions</h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {transactions.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">💰</div>
                    <h3>No transactions recorded</h3>
                    <p>Click &quot;Add Transaction&quot; to record financial transactions.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn) => (
                        <tr key={txn.id}>
                          <td>{new Date(txn.transaction_date).toLocaleDateString()}</td>
                          <td>{txn.description}</td>
                          <td style={{ textTransform: 'capitalize' }}>{txn.category?.replace(/_/g, ' ')}</td>
                          <td>
                            <span className={`status-badge ${txn.transaction_type === 'expense' ? 'inactive' : 'active'}`}>
                              {txn.transaction_type?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={{ 
                            fontWeight: '600',
                            color: txn.transaction_type === 'expense' ? 'var(--error)' : 'var(--success)'
                          }}>
                            {txn.transaction_type === 'expense' ? '-' : '+'}${(txn.amount || 0).toLocaleString()}
                          </td>
                          <td>
                            <button 
                              onClick={() => handleDeleteTransaction(txn.id)}
                              className="btn btn-sm"
                              style={{ background: '#ef4444', color: 'white', padding: '4px 8px' }}
                              title="Delete Transaction"
                              data-testid={`delete-txn-${txn.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Process Payment Tab */}
        {activeTab === 'payments' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                💳 Process Card Payment
              </h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.9375rem' }}>
                Accept debit/credit card payments for tuition, misc fees, deposits, or other charges.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* Payment Form */}
              <div className="card" style={{ padding: '24px' }}>
                <h3 style={{ marginBottom: '20px', color: '#166534' }}>Create Payment</h3>
                
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.target;
                  const formData = {
                    student_name: form.student_name.value,
                    student_email: form.student_email.value,
                    amount: parseFloat(form.amount.value),
                    description: form.description.value,
                    payment_type: form.payment_type.value,
                    program_code: form.program_code.value || null,
                    notes: form.notes.value || null
                  };
                  
                  try {
                    const response = await api.post('/admin/create-payment', formData);
                    if (response.data.checkout_url) {
                      window.open(response.data.checkout_url, '_blank');
                      alert(`Payment link created! Amount: ${response.data.amount_display}\n\nCheckout URL has been opened in a new tab.`);
                    }
                  } catch (err) {
                    alert('Error creating payment: ' + (err.response?.data?.detail || err.message));
                  }
                }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Customer/Student Name *</label>
                    <input 
                      name="student_name"
                      type="text" 
                      required
                      placeholder="John Doe"
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Email Address *</label>
                    <input 
                      name="student_email"
                      type="email" 
                      required
                      placeholder="john@email.com"
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Amount ($) *</label>
                      <input 
                        name="amount"
                        type="number" 
                        step="0.01"
                        min="1"
                        required
                        placeholder="100.00"
                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Payment Type *</label>
                      <select 
                        name="payment_type"
                        required
                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                      >
                        <option value="tuition">Tuition</option>
                        <option value="misc">Misc Fee</option>
                        <option value="deposit">Deposit</option>
                        <option value="registration">Registration Fee</option>
                        <option value="books">Books & Materials</option>
                        <option value="uniform">Uniform</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Description *</label>
                    <input 
                      name="description"
                      type="text" 
                      required
                      placeholder="CMA Tuition Payment, Registration Fee, etc."
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Program (Optional)</label>
                    <select 
                      name="program_code"
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    >
                      <option value="">-- Select Program --</option>
                      <option value="CMA">CMA - Certified Medical Assistant</option>
                      <option value="PN_TECH">PN Tech - Practical Nurse Tech</option>
                      <option value="PHLEBOTOMY">Phlebotomy Technician</option>
                      <option value="EMT">EMT - Emergency Medical Technician</option>
                      <option value="HOMELAND_SECURITY">Master Homeland Security Specialist</option>
                      <option value="COMBINED_HEALTHCARE">Combined Healthcare Program</option>
                    </select>
                  </div>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Notes (Optional)</label>
                    <textarea 
                      name="notes"
                      placeholder="Any additional notes..."
                      rows={2}
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', resize: 'vertical' }}
                    />
                  </div>
                  
                  <button 
                    type="submit"
                    style={{ 
                      width: '100%', 
                      padding: '14px', 
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontSize: '1rem', 
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                    data-testid="create-payment-btn"
                  >
                    💳 Create Payment & Open Checkout
                  </button>
                </form>
              </div>
              
              {/* Quick Payment Buttons */}
              <div>
                <div className="card" style={{ padding: '24px', marginBottom: '16px' }}>
                  <h3 style={{ marginBottom: '16px', color: '#1e40af' }}>Quick Payment Links</h3>
                  <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '16px' }}>
                    Generate payment links for custom amounts
                  </p>
                  
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {[
                      { label: 'Registration Fee', amount: 100, program: '' },
                      { label: 'Books & Materials', amount: 250, program: '' },
                      { label: 'Custom Payment', amount: null, program: '' },
                    ].map((item, idx) => (
                      <button
                        key={idx}
                        onClick={async () => {
                          const email = prompt(`Enter customer email for ${item.label}${item.amount ? ` ($${item.amount.toLocaleString()})` : ''}:`);
                          if (!email) return;
                          const name = prompt('Enter customer name:');
                          if (!name) return;
                          let amount = item.amount;
                          if (!amount) {
                            const inputAmount = prompt('Enter payment amount (numbers only):');
                            if (!inputAmount) return;
                            amount = parseFloat(inputAmount);
                            if (isNaN(amount) || amount <= 0) {
                              alert('Invalid amount');
                              return;
                            }
                          }
                          const description = item.amount ? item.label : prompt('Enter payment description:') || 'Custom Payment';
                          
                          try {
                            const response = await api.post('/admin/create-payment', {
                              student_name: name,
                              student_email: email,
                              amount: amount,
                              description: description,
                              payment_type: 'misc',
                              program_code: item.program || null
                            });
                            if (response.data.checkout_url) {
                              window.open(response.data.checkout_url, '_blank');
                            }
                          } catch (err) {
                            alert('Error: ' + (err.response?.data?.detail || err.message));
                          }
                        }}
                        style={{ 
                          padding: '12px 16px', 
                          background: '#f1f5f9', 
                          border: '1px solid #e2e8f0', 
                          borderRadius: '8px', 
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          textAlign: 'left'
                        }}
                      >
                        <span style={{ fontWeight: '500' }}>{item.label}</span>
                        <span style={{ fontWeight: '700', color: '#16a34a' }}>{item.amount ? `$${item.amount.toLocaleString()}` : 'Custom'}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="card" style={{ padding: '16px', background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                  <h4 style={{ color: '#1e40af', marginBottom: '8px' }}>How It Works</h4>
                  <ol style={{ margin: 0, paddingLeft: '20px', color: '#1d4ed8', fontSize: '0.875rem' }}>
                    <li style={{ marginBottom: '4px' }}>Enter customer details and amount</li>
                    <li style={{ marginBottom: '4px' }}>Click "Create Payment" button</li>
                    <li style={{ marginBottom: '4px' }}>Stripe checkout opens in new tab</li>
                    <li style={{ marginBottom: '4px' }}>Customer enters card details</li>
                    <li>Payment is processed and recorded</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Certificates Tab */}
        {activeTab === 'certificates' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, marginBottom: '8px' }}>Certificate Generation</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.9375rem' }}>
                Generate and download completion certificates for students who have completed all training requirements.
              </p>
            </div>

            {/* CMA Program Certificates */}
            <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid var(--primary-blue)' }}>
              <div className="card-header" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)' }}>
                <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ background: 'var(--primary-blue)', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '0.875rem' }}>CMA</span>
                  CMA Program Certificates
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {enrollments.filter(e => e.status === 'active' && (e.program_code === 'CNA' || !e.program_code)).length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📜</div>
                    <h3>No CMA students enrolled</h3>
                    <p>Students must be enrolled in the CMA program to generate certificates.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Hours Logged</th>
                        <th>Hours Required</th>
                        <th>Progress</th>
                        <th style={{ textAlign: 'center' }}>Generate</th>
                        <th style={{ textAlign: 'center' }}>Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.filter(e => e.status === 'active' && (e.program_code === 'CNA' || !e.program_code)).map((enrollment) => {
                        const student = students.find(s => s.id === enrollment.student_id);
                        const progress = ((enrollment.hours_logged || 0) / (enrollment.total_hours_required || 75) * 100).toFixed(0);
                        const isEligible = parseFloat(progress) >= 100;
                        return (
                          <tr key={enrollment.id} data-testid={`cert-row-cna-${enrollment.id}`}>
                            <td style={{ fontWeight: '500' }}>{student?.full_name || 'Unknown'}</td>
                            <td>{enrollment.hours_logged || 0}</td>
                            <td>{enrollment.total_hours_required || 75}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ 
                                  flex: 1, 
                                  height: '8px', 
                                  background: 'var(--gray-200)', 
                                  borderRadius: '4px',
                                  maxWidth: '100px'
                                }}>
                                  <div style={{ 
                                    width: `${Math.min(progress, 100)}%`, 
                                    height: '100%', 
                                    background: isEligible ? 'var(--success)' : 'var(--primary-blue)',
                                    borderRadius: '4px'
                                  }} />
                                </div>
                                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>{progress}%</span>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => handleGenerateCertificate(enrollment.student_id)}
                                className="btn btn-primary btn-sm"
                                style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                                data-testid={`generate-cert-btn-${enrollment.student_id}`}
                              >
                                <Award size={14} style={{ marginRight: '4px' }} />
                                Generate
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => handleDownloadCertificate(enrollment.student_id, student?.full_name || 'Student')}
                                className="btn btn-secondary btn-sm"
                                style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                                data-testid={`download-cert-btn-${enrollment.student_id}`}
                              >
                                <Download size={14} style={{ marginRight: '4px' }} />
                                Download PDF
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* PN Tech Program Certificates */}
            <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid #10b981' }}>
              <div className="card-header" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)' }}>
                <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ background: '#10b981', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '0.875rem' }}>PN TECH</span>
                  PN Tech Program Certificates
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {enrollments.filter(e => e.status === 'active' && e.program_code === 'PN_TECH').length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📜</div>
                    <h3>No PN Tech students enrolled</h3>
                    <p>Students must be enrolled in the PN Tech program to generate certificates.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Hours Logged</th>
                        <th>Hours Required</th>
                        <th>Progress</th>
                        <th style={{ textAlign: 'center' }}>Generate</th>
                        <th style={{ textAlign: 'center' }}>Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.filter(e => e.status === 'active' && e.program_code === 'PN_TECH').map((enrollment) => {
                        const student = students.find(s => s.id === enrollment.student_id);
                        const progress = ((enrollment.hours_logged || 0) / (enrollment.total_hours_required || 120) * 100).toFixed(0);
                        const isEligible = parseFloat(progress) >= 100;
                        return (
                          <tr key={enrollment.id} data-testid={`cert-row-pntech-${enrollment.id}`}>
                            <td style={{ fontWeight: '500' }}>{student?.full_name || 'Unknown'}</td>
                            <td>{enrollment.hours_logged || 0}</td>
                            <td>{enrollment.total_hours_required || 120}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ 
                                  flex: 1, 
                                  height: '8px', 
                                  background: 'var(--gray-200)', 
                                  borderRadius: '4px',
                                  maxWidth: '100px'
                                }}>
                                  <div style={{ 
                                    width: `${Math.min(progress, 100)}%`, 
                                    height: '100%', 
                                    background: isEligible ? 'var(--success)' : '#10b981',
                                    borderRadius: '4px'
                                  }} />
                                </div>
                                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>{progress}%</span>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => handleGenerateCertificate(enrollment.student_id)}
                                className="btn btn-sm"
                                style={{ padding: '6px 12px', fontSize: '0.875rem', background: '#10b981', color: 'white' }}
                                data-testid={`generate-cert-btn-pntech-${enrollment.student_id}`}
                              >
                                <Award size={14} style={{ marginRight: '4px' }} />
                                Generate
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => handleDownloadCertificate(enrollment.student_id, student?.full_name || 'Student')}
                                className="btn btn-secondary btn-sm"
                                style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                                data-testid={`download-cert-btn-pntech-${enrollment.student_id}`}
                              >
                                <Download size={14} style={{ marginRight: '4px' }} />
                                Download PDF
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* LPN (Hybrid) Program Certificates */}
            <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid #0d9488' }} data-testid="lpn-cert-section">
              <div className="card-header" style={{ background: 'linear-gradient(135deg, #ccfbf1 0%, #ffffff 100%)' }}>
                <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ background: '#0d9488', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '0.875rem' }}>LPN</span>
                  LPN (Hybrid) Program Certificates
                  <span style={{ fontSize: '0.75rem', color: '#0f766e', fontWeight: 'normal' }}>(600 supervised clinical hours · gated by Clinical Affiliation Form verification)</span>
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {enrollments.filter(e => e.status === 'active' && e.program_code === 'LPN').length === 0 ? (
                  <div className="empty-state" style={{ padding: '20px' }}>
                    <p style={{ margin: 0, color: 'var(--gray-500)' }}>No LPN students enrolled yet.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Hours Logged</th>
                        <th>Hours Required</th>
                        <th>Progress</th>
                        <th style={{ textAlign: 'center' }}>Generate</th>
                        <th style={{ textAlign: 'center' }}>Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.filter(e => e.status === 'active' && e.program_code === 'LPN').map((enrollment) => {
                        const student = students.find(s => s.id === enrollment.student_id);
                        const required = enrollment.total_hours_required || 600;
                        const progress = ((enrollment.hours_logged || 0) / required * 100).toFixed(0);
                        const isEligible = parseFloat(progress) >= 100;
                        return (
                          <tr key={enrollment.id} data-testid={`cert-row-lpn-${enrollment.id}`}>
                            <td style={{ fontWeight: '500' }}>{student?.full_name || 'Unknown'}</td>
                            <td>{enrollment.hours_logged || 0}</td>
                            <td>{required}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ flex: 1, height: '8px', background: 'var(--gray-200)', borderRadius: '4px', maxWidth: '100px' }}>
                                  <div style={{ width: `${Math.min(progress, 100)}%`, height: '100%', background: isEligible ? 'var(--success)' : '#0d9488', borderRadius: '4px' }} />
                                </div>
                                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>{progress}%</span>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => handleGenerateCertificate(enrollment.student_id)}
                                className="btn btn-sm"
                                style={{ padding: '6px 12px', fontSize: '0.875rem', background: '#0d9488', color: 'white' }}
                                data-testid={`generate-cert-btn-lpn-${enrollment.student_id}`}
                                title="Generate the LPN certificate. Will fail if Clinical Affiliation Form is not yet verified."
                              >
                                <Award size={14} style={{ marginRight: '4px' }} />
                                Generate
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => handleDownloadCertificate(enrollment.student_id, student?.full_name || 'Student')}
                                className="btn btn-secondary btn-sm"
                                style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                                data-testid={`download-cert-btn-lpn-${enrollment.student_id}`}
                              >
                                <Download size={14} style={{ marginRight: '4px' }} />
                                Download PDF
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Phlebotomy Program Certificates */}
            <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid #f59e0b' }}>
              <div className="card-header" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #ffffff 100%)' }}>
                <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ background: '#f59e0b', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '0.875rem' }}>PHLEBOTOMY</span>
                  Phlebotomy Program Certificates
                  <span style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 'normal' }}>(120 RTI Hours Required)</span>
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {enrollments.filter(e => e.status === 'active' && e.program_code === 'PHLEBOTOMY').length === 0 ? (
                  <div className="empty-state">
                    <p>No Phlebotomy students enrolled yet.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Hours</th>
                        <th>Progress</th>
                        <th style={{ textAlign: 'center' }}>Generate</th>
                        <th style={{ textAlign: 'center' }}>Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.filter(e => e.status === 'active' && e.program_code === 'PHLEBOTOMY').map((enrollment) => {
                        const student = students.find(s => s.id === enrollment.student_id);
                        const progress = ((enrollment.hours_logged || 0) / (enrollment.total_hours_required || 80) * 100).toFixed(0);
                        return (
                          <tr key={enrollment.id}>
                            <td>{student?.full_name || 'Unknown'}</td>
                            <td>{enrollment.hours_logged || 0} / {enrollment.total_hours_required || 80}</td>
                            <td>{progress}%</td>
                            <td style={{ textAlign: 'center' }}>
                              <button onClick={() => handleGenerateCertificate(enrollment.student_id)} className="btn btn-sm" style={{ background: '#f59e0b', color: 'white' }}>
                                Generate
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button onClick={() => handleDownloadCertificate(enrollment.student_id, student?.full_name || 'Student')} className="btn btn-secondary btn-sm">
                                Download PDF
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* EMT Program Certificates */}
            <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid #ef4444' }}>
              <div className="card-header" style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #ffffff 100%)' }}>
                <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ background: '#ef4444', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '0.875rem' }}>EMT</span>
                  EMT Program Certificates
                  <span style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 'normal' }}>(144 RTI Hours Required)</span>
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {enrollments.filter(e => e.status === 'active' && e.program_code === 'EMT').length === 0 ? (
                  <div className="empty-state">
                    <p>No EMT students enrolled yet.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Hours</th>
                        <th>Progress</th>
                        <th style={{ textAlign: 'center' }}>Generate</th>
                        <th style={{ textAlign: 'center' }}>Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.filter(e => e.status === 'active' && e.program_code === 'EMT').map((enrollment) => {
                        const student = students.find(s => s.id === enrollment.student_id);
                        const progress = ((enrollment.hours_logged || 0) / (enrollment.total_hours_required || 120) * 100).toFixed(0);
                        return (
                          <tr key={enrollment.id}>
                            <td>{student?.full_name || 'Unknown'}</td>
                            <td>{enrollment.hours_logged || 0} / {enrollment.total_hours_required || 120}</td>
                            <td>{progress}%</td>
                            <td style={{ textAlign: 'center' }}>
                              <button onClick={() => handleGenerateCertificate(enrollment.student_id)} className="btn btn-sm" style={{ background: '#ef4444', color: 'white' }}>
                                Generate
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button onClick={() => handleDownloadCertificate(enrollment.student_id, student?.full_name || 'Student')} className="btn btn-secondary btn-sm">
                                Download PDF
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Homeland Security Program Certificates */}
            <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid #1a365d' }}>
              <div className="card-header" style={{ background: 'linear-gradient(135deg, #1a365d 0%, #3b82f6 100%)' }}>
                <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'white' }}>
                  <span style={{ background: '#d4af37', color: '#1a365d', padding: '4px 12px', borderRadius: '4px', fontSize: '0.875rem', fontWeight: 'bold' }}>HOMELAND SECURITY</span>
                  Master Homeland Security Specialist Certificates
                  <span style={{ fontSize: '0.75rem', color: '#bfdbfe', fontWeight: 'normal' }}>(154 RTI Hours Required)</span>
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {enrollments.filter(e => e.status === 'active' && e.program_code === 'HOMELAND_SECURITY').length === 0 ? (
                  <div className="empty-state">
                    <p>No Homeland Security students enrolled yet.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Hours</th>
                        <th>Progress</th>
                        <th style={{ textAlign: 'center' }}>Generate</th>
                        <th style={{ textAlign: 'center' }}>Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.filter(e => e.status === 'active' && e.program_code === 'HOMELAND_SECURITY').map((enrollment) => {
                        const student = students.find(s => s.id === enrollment.student_id);
                        const progress = ((enrollment.hours_logged || 0) / (enrollment.total_hours_required || 180) * 100).toFixed(0);
                        return (
                          <tr key={enrollment.id}>
                            <td>{student?.full_name || 'Unknown'}</td>
                            <td>{enrollment.hours_logged || 0} / {enrollment.total_hours_required || 180}</td>
                            <td>{progress}%</td>
                            <td style={{ textAlign: 'center' }}>
                              <button onClick={() => handleGenerateCertificate(enrollment.student_id)} className="btn btn-sm" style={{ background: '#1a365d', color: 'white' }}>
                                Generate
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button onClick={() => handleDownloadCertificate(enrollment.student_id, student?.full_name || 'Student')} className="btn btn-secondary btn-sm">
                                Download PDF
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Info Section */}
            <div className="alert alert-info">
              <p style={{ margin: 0 }}>
                <strong>Note:</strong> Certificates are generated as PDF documents. Click &quot;Generate&quot; to create a new certificate, 
                then &quot;Download PDF&quot; to save or print the certificate.
              </p>
            </div>
          </div>
        )}

        {/* ETA-671 Apprenticeship Agreements & Tuition Contracts Tab */}
        {activeTab === 'agreements' && <AgreementsPanel apiBaseUrl={API_HOST || ''} />}

        {/* Clinical Affiliation Form Review (LPN hybrid) */}
        {activeTab === 'clinical-forms' && <ClinicalFormsPanel />}

        {/* Marketing Posters Tab */}
        {activeTab === 'posters' && <MarketingPostersPanel apiBase={API_HOST || ''} />}

        {/* Student Transcripts Tab */}
        {activeTab === 'transcripts' && <StudentTranscriptsPanel apiBase={API_HOST || ''} />}
        {activeTab === 'doctoral-registry' && <DoctoralRegistryPanel />}
        {activeTab === 'cma-renewals' && <CMARenewalsPanel />}


        {/* GED Credentials Tab */}
        {activeTab === 'ged-credentials' && (
          <div data-testid="ged-credentials-section">
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, marginBottom: '8px', color: '#1e3a5f' }}>GED Credential Certificate & Transcript</h2>
              <p style={{ color: '#64748b', fontSize: '0.9375rem' }}>
                Generate official Tennessee General Equivalency Diploma certificates and transcripts for students.
              </p>
            </div>

            <div className="card" style={{ padding: '32px', borderLeft: '4px solid #7c3aed', marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 24px 0', color: '#7c3aed', display: 'flex', alignItems: 'center', gap: '8px' }}>
                Generate GED Credential
              </h3>

              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const data = {
                  student_name: formData.get('student_name'),
                  date_of_birth: formData.get('date_of_birth') || 'Not on File',
                  issue_date: formData.get('issue_date'),
                  registration_number: formData.get('registration_number') || undefined,
                  include_transcript: formData.get('include_transcript') === 'on'
                };
                try {
                  const response = await fetch(`${API_HOST}/api/ged-credential/generate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data)
                  });
                  if (response.ok) {
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                  } else {
                    const err = await response.json();
                    alert(err.detail || 'Failed to generate credential');
                  }
                } catch (err) {
                  alert('Error generating credential');
                }
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Student Full Name *
                    </label>
                    <input
                      type="text"
                      name="student_name"
                      required
                      placeholder="e.g., John Alexander Doe"
                      data-testid="ged-student-name"
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Date of Birth
                    </label>
                    <input
                      type="text"
                      name="date_of_birth"
                      placeholder="e.g., January 15, 1990 (or leave blank)"
                      data-testid="ged-dob"
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Issue Date *
                    </label>
                    <input
                      type="date"
                      name="issue_date"
                      required
                      defaultValue={new Date().toISOString().split('T')[0]}
                      data-testid="ged-issue-date"
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Registration Number (auto if blank)
                    </label>
                    <input
                      type="text"
                      name="registration_number"
                      placeholder="e.g., 2026-TN-137933"
                      data-testid="ged-reg-number"
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" name="include_transcript" defaultChecked data-testid="ged-include-transcript" />
                    <span style={{ fontSize: '0.9rem', color: '#374151' }}>Include Transcript (Page 2 with Subject Area Results)</span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    data-testid="ged-generate-btn"
                    style={{
                      background: '#7c3aed', color: 'white', padding: '12px 28px',
                      border: 'none', borderRadius: '8px', cursor: 'pointer',
                      fontWeight: 'bold', fontSize: '0.95rem'
                    }}
                  >
                    Generate & Print Credential
                  </button>
                  <a
                    href={`${API_HOST}/api/ged-credential/preview`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ged-preview-btn"
                    style={{
                      background: '#f3e8ff', color: '#7c3aed', padding: '12px 28px',
                      border: '1px solid #7c3aed', borderRadius: '8px', cursor: 'pointer',
                      fontWeight: 'bold', fontSize: '0.95rem', textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center'
                    }}
                  >
                    Preview Sample
                  </a>
                </div>
              </form>
            </div>

            <div className="card" style={{ padding: '24px', background: '#faf5ff', border: '1px solid #e9d5ff' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#7c3aed' }}>Credential Details</h4>
              <div style={{ fontSize: '0.9rem', color: '#4a5568', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 8px 0' }}><strong>Certificate (Page 1):</strong> Official "General Equivalency Diploma" from the State of Tennessee with Old English typography, state seal, registration number, and formal language.</p>
                <p style={{ margin: '0 0 8px 0' }}><strong>Transcript (Page 2):</strong> Subject Area Results showing all 5 GED test areas (Language Arts – Reading, Language Arts – Writing, Mathematics, Science, Social Studies) with "Passed" status.</p>
                <p style={{ margin: 0 }}><strong>Includes:</strong> Tennessee State Seal, Dept of Labor & Workforce Development authorization, unique registration number.</p>
              </div>
            </div>
          </div>
        )}

        {/* Training Modules Tab */}
        {activeTab === 'modules' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2>Training Modules</h2>
              <Link to="/manage-modules" className="btn btn-primary" data-testid="edit-modules-link">
                <Plus size={16} style={{ marginRight: '8px' }} />
                Add/Edit Modules
              </Link>
            </div>
            
            {/* CMA Modules Section */}
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header">
                <h3 className="card-title">CMA Program Modules ({modules.filter(m => m.program_code === 'CNA').length})</h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {modules.filter(m => m.program_code === 'CNA').length === 0 ? (
                  <div className="empty-state">
                    <BookOpen size={48} style={{ color: 'var(--gray-400)', marginBottom: '16px' }} />
                    <p>No CMA modules found.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>#</th>
                        <th>Title</th>
                        <th>Duration</th>
                        <th>Video</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules
                        .filter(m => m.program_code === 'CNA')
                        .sort((a, b) => a.module_number - b.module_number)
                        .map(module => (
                          <tr key={module.id}>
                            <td><strong>{module.module_number}</strong></td>
                            <td>
                              <div style={{ fontWeight: '500' }}>{module.title}</div>
                              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                                {module.description?.substring(0, 80)}{module.description?.length > 80 ? '...' : ''}
                              </div>
                            </td>
                            <td>{module.duration_minutes} min</td>
                            <td>
                              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Reading only</span>
                            </td>
                            <td>
                              <Link 
                                to={`/module/${module.id}`} 
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: '0.75rem' }}
                              >
                                View Details
                              </Link>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            
            {/* PN Tech Modules Section */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  PN Tech Program Modules ({modules.filter(m => m.program_code === 'PN_TECH').length})
                  <span className="status-badge" style={{ marginLeft: '12px', background: '#10b981', color: 'white' }}>Pre-LPN</span>
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {modules.filter(m => m.program_code === 'PN_TECH').length === 0 ? (
                  <div className="empty-state">
                    <BookOpen size={48} style={{ color: 'var(--gray-400)', marginBottom: '16px' }} />
                    <p>PN Tech modules will be available when the program launches.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>#</th>
                        <th>Title</th>
                        <th>Duration</th>
                        <th>Video</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules
                        .filter(m => m.program_code === 'PN_TECH')
                        .sort((a, b) => a.module_number - b.module_number)
                        .map(module => (
                          <tr key={module.id}>
                            <td><strong>{module.module_number}</strong></td>
                            <td>
                              <div style={{ fontWeight: '500' }}>{module.title}</div>
                              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                                {module.description?.substring(0, 80)}{module.description?.length > 80 ? '...' : ''}
                              </div>
                            </td>
                            <td>{module.duration_minutes} min</td>
                            <td>
                              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Reading only</span>
                            </td>
                            <td>
                              <Link 
                                to={`/module/${module.id}`} 
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: '0.75rem' }}
                              >
                                View Details
                              </Link>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            
            {/* Phlebotomy Modules Section */}
            <div className="card" style={{ marginTop: '24px' }}>
              <div className="card-header" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #ffffff 100%)' }}>
                <h3 className="card-title">
                  Phlebotomy Program Modules ({modules.filter(m => m.program_code === 'PHLEBOTOMY').length})
                  <span className="status-badge" style={{ marginLeft: '12px', background: '#f59e0b', color: 'white' }}>120 RTI Hours</span>
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {modules.filter(m => m.program_code === 'PHLEBOTOMY').length === 0 ? (
                  <div className="empty-state">
                    <BookOpen size={48} style={{ color: 'var(--gray-400)', marginBottom: '16px' }} />
                    <p>Phlebotomy modules loading...</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>#</th>
                        <th>Title</th>
                        <th>Duration</th>
                        <th>Video</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules
                        .filter(m => m.program_code === 'PHLEBOTOMY')
                        .sort((a, b) => a.module_number - b.module_number)
                        .map(module => (
                          <tr key={module.id}>
                            <td><strong>{module.module_number}</strong></td>
                            <td>
                              <div style={{ fontWeight: '500' }}>{module.title}</div>
                              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                                {module.description?.substring(0, 80)}{module.description?.length > 80 ? '...' : ''}
                              </div>
                            </td>
                            <td>{module.duration_minutes} min</td>
                            <td>
                              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Reading only</span>
                            </td>
                            <td>
                              <Link to={`/module/${module.id}`} className="btn btn-sm btn-primary" style={{ fontSize: '0.75rem' }}>View Details</Link>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            
            {/* EMT Modules Section */}
            <div className="card" style={{ marginTop: '24px' }}>
              <div className="card-header" style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #ffffff 100%)' }}>
                <h3 className="card-title">
                  EMT Program Modules ({modules.filter(m => m.program_code === 'EMT').length})
                  <span className="status-badge" style={{ marginLeft: '12px', background: '#ef4444', color: 'white' }}>144 RTI Hours</span>
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {modules.filter(m => m.program_code === 'EMT').length === 0 ? (
                  <div className="empty-state">
                    <BookOpen size={48} style={{ color: 'var(--gray-400)', marginBottom: '16px' }} />
                    <p>EMT modules loading...</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>#</th>
                        <th>Title</th>
                        <th>Duration</th>
                        <th>Video</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules
                        .filter(m => m.program_code === 'EMT')
                        .sort((a, b) => a.module_number - b.module_number)
                        .map(module => (
                          <tr key={module.id}>
                            <td><strong>{module.module_number}</strong></td>
                            <td>
                              <div style={{ fontWeight: '500' }}>{module.title}</div>
                              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                                {module.description?.substring(0, 80)}{module.description?.length > 80 ? '...' : ''}
                              </div>
                            </td>
                            <td>{module.duration_minutes} min</td>
                            <td>
                              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Reading only</span>
                            </td>
                            <td>
                              <Link to={`/module/${module.id}`} className="btn btn-sm btn-primary" style={{ fontSize: '0.75rem' }}>View Details</Link>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            
            {/* Homeland Security Modules Section */}
            <div className="card" style={{ marginTop: '24px' }}>
              <div className="card-header" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #3b82f6 100%)', color: 'white' }}>
                <h3 className="card-title" style={{ color: 'white' }}>
                  Master Homeland Security Specialist ({modules.filter(m => m.program_code === 'HOMELAND_SECURITY').length})
                  <span className="status-badge" style={{ marginLeft: '12px', background: '#d4af37', color: '#1e3a5f' }}>154 RTI Hours</span>
                </h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {modules.filter(m => m.program_code === 'HOMELAND_SECURITY').length === 0 ? (
                  <div className="empty-state">
                    <BookOpen size={48} style={{ color: 'var(--gray-400)', marginBottom: '16px' }} />
                    <p>Homeland Security modules loading...</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>#</th>
                        <th>Title</th>
                        <th>Duration</th>
                        <th>Video</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules
                        .filter(m => m.program_code === 'HOMELAND_SECURITY')
                        .sort((a, b) => a.module_number - b.module_number)
                        .map(module => (
                          <tr key={module.id}>
                            <td><strong>{module.module_number}</strong></td>
                            <td>
                              <div style={{ fontWeight: '500' }}>{module.title}</div>
                              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                                {module.description?.substring(0, 80)}{module.description?.length > 80 ? '...' : ''}
                              </div>
                            </td>
                            <td>{module.duration_minutes} min</td>
                            <td>
                              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Reading only</span>
                            </td>
                            <td>
                              <Link to={`/module/${module.id}`} className="btn btn-sm btn-primary" style={{ fontSize: '0.75rem' }}>View Details</Link>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div>
            <h2 style={{ marginBottom: '24px' }}>Reports</h2>
            
            <div className="modules-grid">
              <div className="module-card">
                <FileText size={32} style={{ color: 'var(--primary-blue)', marginBottom: '16px' }} />
                <h3>Enrollment Report</h3>
                <p>Summary of all student enrollments by program and status.</p>
                <button 
                  onClick={() => handleDownloadReport('enrollment', 'enrollment_report')}
                  className="btn-module" 
                  style={{ marginTop: '16px' }} 
                  data-testid="enrollment-report-btn"
                >
                  <Download size={16} style={{ marginRight: '8px' }} />
                  Download CSV
                </button>
              </div>
              
              <div className="module-card">
                <Clock size={32} style={{ color: 'var(--primary-blue)', marginBottom: '16px' }} />
                <h3>Hours Report</h3>
                <p>Detailed breakdown of hours logged by student and activity type.</p>
                <button 
                  onClick={() => handleDownloadReport('hours', 'hours_report')}
                  className="btn-module" 
                  style={{ marginTop: '16px' }} 
                  data-testid="hours-report-btn"
                >
                  <Download size={16} style={{ marginRight: '8px' }} />
                  Download CSV
                </button>
              </div>
              
              <div className="module-card">
                <DollarSign size={32} style={{ color: 'var(--primary-blue)', marginBottom: '16px' }} />
                <h3>Financial Report</h3>
                <p>Income, expenses, and grant funds received summary.</p>
                <button 
                  onClick={() => handleDownloadReport('financial', 'financial_report')}
                  className="btn-module" 
                  style={{ marginTop: '16px' }} 
                  data-testid="financial-report-btn"
                >
                  <Download size={16} style={{ marginRight: '8px' }} />
                  Download CSV
                </button>
              </div>
              
              <div className="module-card">
                <Award size={32} style={{ color: 'var(--primary-blue)', marginBottom: '16px' }} />
                <h3>Completion Report</h3>
                <p>Students who have completed training and received certificates.</p>
                <button 
                  onClick={() => handleDownloadReport('completion', 'completion_report')}
                  className="btn-module" 
                  style={{ marginTop: '16px' }} 
                  data-testid="completion-report-btn"
                >
                  <Download size={16} style={{ marginRight: '8px' }} />
                  Download CSV
                </button>
              </div>
              
              <div className="module-card">
                <TrendingUp size={32} style={{ color: 'var(--primary-blue)', marginBottom: '16px' }} />
                <h3>Progress Report</h3>
                <p>Student progress through training modules and tests.</p>
                <button 
                  onClick={() => handleDownloadReport('progress', 'progress_report')}
                  className="btn-module" 
                  style={{ marginTop: '16px' }} 
                  data-testid="progress-report-btn"
                >
                  <Download size={16} style={{ marginRight: '8px' }} />
                  Download CSV
                </button>
              </div>
              
              <div className="module-card">
                <Users size={32} style={{ color: 'var(--primary-blue)', marginBottom: '16px' }} />
                <h3>Grant Utilization</h3>
                <p>WIOA and non-WIOA grant funds received and allocated.</p>
                <button 
                  onClick={() => handleDownloadReport('grants', 'grants_report')}
                  className="btn-module" 
                  style={{ marginTop: '16px' }} 
                  data-testid="grant-report-btn"
                >
                  <Download size={16} style={{ marginRight: '8px' }} />
                  Download CSV
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Online Courses Management Tab */}
        {activeTab === 'online-courses' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, marginBottom: '8px' }}>Online Courses</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.9375rem' }}>
                Manage online course enrollments, track student progress, and view revenue.
              </p>
            </div>

            {/* Stats Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <p style={{ margin: 0, color: 'var(--gray-500)', fontSize: '0.875rem' }}>Total Enrollments</p>
                <p style={{ margin: 0, fontSize: '2rem', fontWeight: '700', color: 'var(--primary-navy)' }}>
                  {onlineCourses.stats?.total_enrollments || 0}
                </p>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <p style={{ margin: 0, color: 'var(--gray-500)', fontSize: '0.875rem' }}>Completed</p>
                <p style={{ margin: 0, fontSize: '2rem', fontWeight: '700', color: '#22c55e' }}>
                  {onlineCourses.stats?.completed || 0}
                </p>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <p style={{ margin: 0, color: 'var(--gray-500)', fontSize: '0.875rem' }}>In Progress</p>
                <p style={{ margin: 0, fontSize: '2rem', fontWeight: '700', color: '#f59e0b' }}>
                  {onlineCourses.stats?.in_progress || 0}
                </p>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <p style={{ margin: 0, color: 'var(--gray-500)', fontSize: '0.875rem' }}>Total Revenue</p>
                <p style={{ margin: 0, fontSize: '2rem', fontWeight: '700', color: 'var(--primary-navy)' }}>
                  ${(onlineCourses.stats?.total_revenue || 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Course Breakdown */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin: '0 0 16px 0', color: 'var(--primary-navy)' }}>Course Breakdown</h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                {Object.entries(onlineCourses.course_stats || {}).map(([courseId, stats]) => (
                  <div key={courseId} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: 'var(--primary-navy)' }}>{stats.short_title || stats.title}</p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)' }}>{stats.title}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '24px', fontSize: '0.875rem' }}>
                      <span><strong>{stats.total_enrolled || 0}</strong> enrolled</span>
                      <span style={{ color: '#22c55e' }}><strong>{stats.completed || 0}</strong> completed</span>
                      <span style={{ color: '#f59e0b' }}><strong>{stats.in_progress || 0}</strong> in progress</span>
                      <span><strong>${(stats.revenue || 0).toLocaleString()}</strong> revenue</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Enrollments Table */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ margin: 0, color: 'var(--primary-navy)' }}>All Enrollments</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn"
                    onClick={() => setShowManualEnrollModal(true)}
                    data-testid="manual-enroll-btn"
                    style={{ background: '#059669', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Plus size={16} />
                    Enroll Student (Free)
                  </button>
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      width: '250px'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Student</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Course</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Apprentice ID</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600' }}>Progress</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Enrolled</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>Paid</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(onlineCourses.enrollments || [])
                      .filter(e => 
                        !searchTerm || 
                        e.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        e.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        e.apprentice_id?.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((enrollment) => (
                        <tr key={enrollment.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 8px' }}>
                            <div>
                              <p style={{ margin: 0, fontWeight: '500' }}>{enrollment.user_name}</p>
                              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)' }}>{enrollment.user_email}</p>
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <span style={{ 
                              background: '#f0f9ff', 
                              color: '#0369a1', 
                              padding: '4px 8px', 
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}>
                              {enrollment.course_details?.short_title || enrollment.course_title}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {enrollment.apprentice_id}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                              <div style={{ 
                                width: '60px', 
                                height: '6px', 
                                background: '#e2e8f0', 
                                borderRadius: '3px',
                                overflow: 'hidden'
                              }}>
                                <div style={{ 
                                  width: `${(enrollment.modules_completed / enrollment.total_modules) * 100}%`, 
                                  height: '100%', 
                                  background: enrollment.completed_at ? '#22c55e' : '#3b82f6',
                                  borderRadius: '3px'
                                }} />
                              </div>
                              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                                {enrollment.modules_completed}/{enrollment.total_modules}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              background: enrollment.completed_at ? '#dcfce7' : enrollment.status === 'active' ? '#fef3c7' : '#fee2e2',
                              color: enrollment.completed_at ? '#166534' : enrollment.status === 'active' ? '#92400e' : '#991b1b'
                            }}>
                              {enrollment.completed_at ? 'Completed' : enrollment.status === 'active' ? 'In Progress' : enrollment.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                            {enrollment.enrolled_at ? new Date(enrollment.enrolled_at).toLocaleDateString() : '-'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '500' }}>
                            {enrollment.payment_waived ? (
                              <span style={{ color: '#059669', fontSize: '0.75rem' }}>Waived</span>
                            ) : (
                              `$${enrollment.amount_paid || 0}`
                            )}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => setProgressUserId(enrollment.user_id)}
                              data-testid={`view-progress-btn-${enrollment.id}`}
                              style={{
                                padding: '4px 8px',
                                fontSize: '0.7rem',
                                background: '#0b2545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: '500',
                              }}
                              title="View full student progress"
                            >
                              Progress
                            </button>
                            {!enrollment.payment_waived && (
                              <button
                                onClick={async () => {
                                  const reason = prompt('Reason for waiving the $199 enrollment fee? (optional)') || '';
                                  try {
                                    const resp = await api.post(`/admin/online-courses/${enrollment.course_id}/waive-enrollment/${enrollment.user_id}?reason=${encodeURIComponent(reason)}`);
                                    if (resp.status === 200) {
                                      alert('Enrollment fee waived.');
                                      fetchData();
                                    }
                                  } catch (err) {
                                    alert(err.response?.data?.detail || 'Error waiving enrollment');
                                  }
                                }}
                                data-testid={`waive-enrollment-btn-${enrollment.id}`}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '0.7rem',
                                  background: '#0d9488',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                }}
                                title="Waive the $199 program-enrollment fee"
                              >
                                Waive Enroll
                              </button>
                            )}
                            {!enrollment.midterm_paid && (
                              <button
                                onClick={async () => {
                                  const reason = prompt('Reason for waiving the mid-term exam fee? (optional)') || '';
                                  try {
                                    const resp = await api.post(`/admin/online-courses/${enrollment.course_id}/waive-exam/${enrollment.user_id}?exam_type=midterm&reason=${encodeURIComponent(reason)}`);
                                    if (resp.status === 200) {
                                      alert('Mid-term exam fee waived.');
                                      fetchData();
                                    }
                                  } catch (err) {
                                    alert(err.response?.data?.detail || 'Error waiving mid-term fee');
                                  }
                                }}
                                data-testid={`waive-midterm-btn-${enrollment.id}`}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '0.7rem',
                                  background: '#0891b2',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                }}
                                title="Waive the mid-term exam fee"
                              >
                                Waive Mid-term
                              </button>
                            )}
                            {!enrollment.final_paid && (
                              <button
                                onClick={async () => {
                                  const reason = prompt('Reason for waiving the final exam fee? (optional)') || '';
                                  try {
                                    const resp = await api.post(`/admin/online-courses/${enrollment.course_id}/waive-exam/${enrollment.user_id}?exam_type=final&reason=${encodeURIComponent(reason)}`);
                                    if (resp.status === 200) {
                                      alert('Final exam fee waived.');
                                      fetchData();
                                    }
                                  } catch (err) {
                                    alert(err.response?.data?.detail || 'Error waiving final fee');
                                  }
                                }}
                                data-testid={`waive-final-btn-${enrollment.id}`}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '0.7rem',
                                  background: '#7c3aed',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                }}
                                title="Waive the final exam fee"
                              >
                                Waive Final
                              </button>
                            )}
                            {!enrollment.completed_at ? (
                              <button
                                onClick={() => handleForceComplete(enrollment.id)}
                                data-testid={`force-complete-btn-${enrollment.id}`}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '0.7rem',
                                  background: '#7c3aed',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: '500'
                                }}
                                title="Force complete for testing"
                              >
                                Test Complete
                              </button>
                            ) : (
                              <>
                                {!enrollment.admin_approved ? (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const resp = await api.post(`/admin/online-courses/${enrollment.course_id}/approve-certificate/${enrollment.user_id}`);
                                        if (resp.status === 200) {
                                          alert('Certificate approved!');
                                          fetchOnlineCourses();
                                        }
                                      } catch (err) {
                                        alert(err.response?.data?.detail || 'Error approving');
                                      }
                                    }}
                                    data-testid={`approve-cert-btn-${enrollment.id}`}
                                    style={{
                                      padding: '4px 8px',
                                      fontSize: '0.7rem',
                                      background: '#f59e0b',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    Approve Cert
                                  </button>
                                ) : (
                                  <span style={{ fontSize: '0.7rem', color: '#22c55e', fontWeight: '600' }}>Approved</span>
                                )}
                                <a
                                  href={`${API_HOST}/api/online-courses/${enrollment.course_id}/certificate/${enrollment.user_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`view-cert-btn-${enrollment.id}`}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '0.7rem',
                                    background: '#22c55e',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    textDecoration: 'none'
                                  }}
                                >
                                  View Cert
                                </a>
                              </>
                            )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                
                {(!onlineCourses.enrollments || onlineCourses.enrollments.length === 0) && (
                  <p style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                    No online course enrollments yet.
                  </p>
                )}
              </div>
            </div>

            {/* Certificates Section */}
            <div data-testid="generated-certificates-section" style={{ background: 'white', borderRadius: '12px', padding: '24px', marginTop: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 data-testid="generated-certificates-title" style={{ margin: 0, color: 'var(--primary-navy)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🏆 Generated Certificates ({onlineCertificates.total_certificates})
                </h3>
              </div>
              
              {onlineCertificates.certificates && onlineCertificates.certificates.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table data-testid="generated-certificates-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Student</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Course</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Apprentice ID</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Verification Code</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Completed</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {onlineCertificates.certificates.map((cert) => (
                        <tr key={cert.id} data-testid={`online-cert-row-${cert.id}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 8px' }}>
                            <div>
                              <p style={{ margin: 0, fontWeight: '500' }} data-testid={`cert-student-name-${cert.id}`}>{cert.student_name}</p>
                              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)' }} data-testid={`cert-student-email-${cert.id}`}>{cert.student_email}</p>
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <span data-testid={`cert-course-title-${cert.id}`} style={{ 
                              background: '#dcfce7', 
                              color: '#166534', 
                              padding: '4px 8px', 
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}>
                              {cert.short_title || cert.course_title}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', fontFamily: 'monospace', fontSize: '0.8rem' }} data-testid={`cert-apprentice-id-${cert.id}`}>
                            {cert.apprentice_id}
                          </td>
                          <td style={{ padding: '12px 8px', fontFamily: 'monospace', fontSize: '0.8rem', color: '#6366f1' }} data-testid={`cert-verification-code-${cert.id}`}>
                            {cert.verification_code}
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                            {cert.completed_at ? new Date(cert.completed_at).toLocaleDateString() : '-'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <a
                              href={`${API_HOST}${cert.download_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`download-online-cert-${cert.id}`}
                              style={{
                                padding: '6px 12px',
                                background: '#22c55e',
                                color: 'white',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}
                            >
                              Download PDF
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p data-testid="no-certificates-message" style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                  No certificates generated yet. Certificates are auto-generated when students complete all course modules.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Employers Tab */}
        {activeTab === 'employers' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, marginBottom: '8px' }}>Employer Partners</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.9375rem' }}>
                Manage employer registrations for OJT and post-training hiring.
              </p>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                {employers.length === 0 ? (
                  <div className="empty-state" style={{ padding: '48px 24px' }}>
                    <div className="empty-state-icon"><Building2 size={48} /></div>
                    <h3>No employer registrations yet</h3>
                    <p>Employers can register at the /employers page.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Contact</th>
                        <th>Industry</th>
                        <th>Interests</th>
                        <th>Status</th>
                        <th>Registered</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employers.map((employer) => (
                        <tr key={employer.id} data-testid={`employer-row-${employer.id}`}>
                          <td>
                            <div style={{ fontWeight: '500' }}>{employer.company_name}</div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                              {employer.city}, {employer.state} {employer.zip_code}
                            </div>
                          </td>
                          <td>
                            <div>{employer.contact_name}</div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                              <a href={`mailto:${employer.email}`}>{employer.email}</a>
                            </div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                              {employer.phone}
                            </div>
                          </td>
                          <td style={{ textTransform: 'capitalize' }}>{employer.industry?.replace('_', ' ')}</td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {employer.ojt_interest && <span className="status-badge active" style={{ fontSize: '0.75rem' }}>OJT</span>}
                              {employer.post_training_interest && <span className="status-badge" style={{ fontSize: '0.75rem', background: 'var(--primary-blue)', color: 'white' }}>Post-Training</span>}
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${employer.status === 'pending' ? 'inactive' : employer.status === 'active' ? 'active' : ''}`}>
                              {employer.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                            {new Date(employer.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <select
                              value={employer.status}
                              onChange={(e) => handleUpdateEmployerStatus(employer.id, e.target.value)}
                              className="form-input"
                              style={{ fontSize: '0.8125rem', padding: '4px 8px' }}
                              data-testid={`employer-status-${employer.id}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contacted</option>
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Partners Tab */}
        {activeTab === 'partners' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, marginBottom: '8px' }}>Partner &amp; Donation Inquiries</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.9375rem' }}>
                Manage partnership inquiries and donation requests via True Victory Ministries.
              </p>
            </div>

            {/* Quick Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div className="stat-card">
                <div className="stat-value">{partners.filter(p => p.status === 'pending').length}</div>
                <div className="stat-label">Pending Inquiries</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{partners.filter(p => p.interest_type === 'student_sponsorship').length}</div>
                <div className="stat-label">Student Sponsors</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{partners.filter(p => p.interest_type === 'donation').length}</div>
                <div className="stat-label">Donations</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{partners.filter(p => p.interest_type === 'corporate_partnership').length}</div>
                <div className="stat-label">Corporate Partners</div>
              </div>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                {partners.length === 0 ? (
                  <div className="empty-state" style={{ padding: '48px 24px' }}>
                    <div className="empty-state-icon"><Heart size={48} /></div>
                    <h3>No partner inquiries yet</h3>
                    <p>Partners can submit inquiries at the /partner page.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Name / Organization</th>
                        <th>Type</th>
                        <th>Interest</th>
                        <th>Contact</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partners.map((partner) => (
                        <tr key={partner.id} data-testid={`partner-row-${partner.id}`}>
                          <td>
                            <div style={{ fontWeight: '500' }}>{partner.name}</div>
                            {partner.organization && (
                              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                                {partner.organization}
                              </div>
                            )}
                          </td>
                          <td style={{ textTransform: 'capitalize' }}>{partner.partner_type}</td>
                          <td>
                            <span className="status-badge" style={{ 
                              background: partner.interest_type === 'student_sponsorship' ? '#7c3aed' : 
                                         partner.interest_type === 'donation' ? '#dc2626' : 
                                         partner.interest_type === 'corporate_partnership' ? 'var(--primary-blue)' : 'var(--gray-500)',
                              color: 'white',
                              fontSize: '0.75rem'
                            }}>
                              {partner.interest_type?.replace('_', ' ')}
                            </span>
                            {partner.sponsorship_type && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                                {partner.sponsorship_type.replace('_', ' ')}
                              </div>
                            )}
                            {partner.donation_amount && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: '4px' }}>
                                {partner.donation_amount}
                              </div>
                            )}
                          </td>
                          <td>
                            <div style={{ fontSize: '0.8125rem' }}>
                              <a href={`mailto:${partner.email}`}>{partner.email}</a>
                            </div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                              {partner.phone}
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${partner.status === 'pending' ? 'inactive' : partner.status === 'active' ? 'active' : ''}`}>
                              {partner.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                            {new Date(partner.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <select
                              value={partner.status}
                              onChange={(e) => handleUpdatePartnerStatus(partner.id, e.target.value)}
                              className="form-input"
                              style={{ fontSize: '0.8125rem', padding: '4px 8px' }}
                              data-testid={`partner-status-${partner.id}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contacted</option>
                              <option value="active">Active</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Donation Info */}
            <div className="alert alert-info" style={{ marginTop: '24px' }}>
              <p style={{ margin: 0 }}>
                <strong>Donation Instructions:</strong> All donations should be directed to 
                <strong> True Victory Ministries</strong>, 3150 E. Shelby Dr., Memphis, TN 38118 
                (TN Non-Profit Organization).
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Log Hours Modal */}
      {showHoursModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>Log Student Hours</h2>
              <button onClick={() => setShowHoursModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleLogHours}>
              <div className="form-group">
                <label className="form-label">Student *</label>
                <select
                  value={hoursForm.student_id}
                  onChange={(e) => handleStudentSelect(e.target.value)}
                  className="form-input"
                  required
                  data-testid="hours-student-select"
                >
                  <option value="">Select a student</option>
                  {enrolledStudents.map(student => (
                    <option key={student.id} value={student.id}>{student.full_name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  value={hoursForm.date}
                  onChange={(e) => setHoursForm(prev => ({ ...prev, date: e.target.value }))}
                  className="form-input"
                  required
                  data-testid="hours-date-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Hours *</label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="12"
                  value={hoursForm.hours}
                  onChange={(e) => setHoursForm(prev => ({ ...prev, hours: e.target.value }))}
                  className="form-input"
                  placeholder="Enter hours (e.g., 4)"
                  required
                  data-testid="hours-amount-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Activity Type *</label>
                <select
                  value={hoursForm.activity_type}
                  onChange={(e) => setHoursForm(prev => ({ ...prev, activity_type: e.target.value }))}
                  className="form-input"
                  required
                  data-testid="hours-activity-select"
                >
                  <option value="classroom">Classroom</option>
                  <option value="clinical">Clinical</option>
                  <option value="lab">Lab</option>
                  <option value="study">Study</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  value={hoursForm.description}
                  onChange={(e) => setHoursForm(prev => ({ ...prev, description: e.target.value }))}
                  className="form-input"
                  placeholder="Optional notes about the activity"
                  rows={3}
                  data-testid="hours-description-input"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowHoursModal(false)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="hours-submit-btn">
                  Log Hours
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Transaction Modal */}
      {showTransactionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>Add Transaction</h2>
              <button onClick={() => setShowTransactionModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleAddTransaction}>
              <div className="form-group">
                <label className="form-label">Transaction Type *</label>
                <select
                  value={transactionForm.transaction_type}
                  onChange={(e) => setTransactionForm(prev => ({ ...prev, transaction_type: e.target.value }))}
                  className="form-input"
                  required
                  data-testid="transaction-type-select"
                >
                  <option value="tuition_payment">Tuition Payment</option>
                  <option value="grant_received">Grant Received</option>
                  <option value="expense">Expense</option>
                  <option value="revenue">Other Revenue</option>
                  <option value="refund">Refund</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Category *</label>
                <select
                  value={transactionForm.category}
                  onChange={(e) => setTransactionForm(prev => ({ ...prev, category: e.target.value }))}
                  className="form-input"
                  required
                  data-testid="transaction-category-select"
                >
                  <optgroup label="Income">
                    <option value="student_payment">Student Payment</option>
                    <option value="wioa_grant">WIOA Grant</option>
                    <option value="snap_et">SNAP E&T</option>
                    <option value="tanf">TANF Workforce</option>
                    <option value="apprenticeship_grant">Apprenticeship Grant</option>
                    <option value="healthcare_pipeline">Healthcare Pipeline Grant</option>
                    <option value="veterans_fund">Veterans Training Fund</option>
                    <option value="reentry_grant">Reentry Grant</option>
                    <option value="other_grant">Other Grant</option>
                  </optgroup>
                  <optgroup label="Expenses">
                    <option value="supplies">Supplies</option>
                    <option value="payroll">Payroll</option>
                    <option value="rent">Rent/Utilities</option>
                    <option value="equipment">Equipment</option>
                    <option value="marketing">Marketing</option>
                    <option value="insurance">Insurance</option>
                    <option value="other_expense">Other Expense</option>
                  </optgroup>
                </select>
              </div>

              {(transactionForm.transaction_type === 'tuition_payment' || transactionForm.transaction_type === 'grant_received') && (
                <div className="form-group">
                  <label className="form-label">Student (Optional)</label>
                  <select
                    value={transactionForm.student_id}
                    onChange={(e) => handleTransactionStudentSelect(e.target.value)}
                    className="form-input"
                    data-testid="transaction-student-select"
                  >
                    <option value="">Select a student (optional)</option>
                    {students.map(student => (
                      <option key={student.id} value={student.id}>{student.full_name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={transactionForm.amount}
                  onChange={(e) => setTransactionForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="form-input"
                  placeholder="Enter amount"
                  required
                  data-testid="transaction-amount-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Description *</label>
                <input
                  type="text"
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm(prev => ({ ...prev, description: e.target.value }))}
                  className="form-input"
                  placeholder="Transaction description"
                  required
                  data-testid="transaction-description-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select
                  value={transactionForm.payment_method}
                  onChange={(e) => setTransactionForm(prev => ({ ...prev, payment_method: e.target.value }))}
                  className="form-input"
                  data-testid="transaction-method-select"
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="card">Credit/Debit Card</option>
                  <option value="wire">Wire Transfer</option>
                  <option value="grant">Grant Disbursement</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Reference Number</label>
                <input
                  type="text"
                  value={transactionForm.reference_number}
                  onChange={(e) => setTransactionForm(prev => ({ ...prev, reference_number: e.target.value }))}
                  className="form-input"
                  placeholder="Check #, receipt #, etc."
                  data-testid="transaction-reference-input"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowTransactionModal(false)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="transaction-submit-btn">
                  Add Transaction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditStudentModal && editingStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>Edit Student: {editingStudent.full_name}</h2>
              <button onClick={() => setShowEditStudentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            
            {/* Apprentice ID Section */}
            <div style={{ marginBottom: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: 'var(--primary-blue)' }}>DOL Apprentice ID</h3>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label">Apprentice ID</label>
                <input
                  type="text"
                  value={editStudentForm.apprentice_id}
                  onChange={(e) => setEditStudentForm(prev => ({ ...prev, apprentice_id: e.target.value }))}
                  className="form-input"
                  placeholder="e.g., TN-APP-2026-001234"
                  data-testid="edit-apprentice-id-input"
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                  Enter the Department of Labor assigned Apprentice ID
                </p>
              </div>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleUpdateApprenticeId}
                style={{ width: '100%' }}
                data-testid="update-apprentice-id-btn"
              >
                Update Apprentice ID
              </button>
            </div>
            
            {/* Certificate Completion Date Section */}
            <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: '#10b981' }}>Certificate Award Date</h3>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label">Completion/Award Date</label>
                <input
                  type="date"
                  value={editStudentForm.completion_date}
                  onChange={(e) => setEditStudentForm(prev => ({ ...prev, completion_date: e.target.value }))}
                  className="form-input"
                  data-testid="edit-completion-date-input"
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                  This date will appear on the certificate as the award date
                </p>
              </div>
              <button 
                type="button" 
                className="btn" 
                onClick={handleUpdateCompletionDate}
                style={{ width: '100%', background: '#10b981', color: 'white' }}
                data-testid="update-completion-date-btn"
              >
                Update Certificate Date
              </button>
            </div>
            
            <div style={{ marginTop: '24px' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowEditStudentModal(false)} 
                style={{ width: '100%' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Online Course Enrollment Modal */}
      {showManualEnrollModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }} data-testid="manual-enroll-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: 'var(--primary-navy)' }}>🎓 Enroll Student in Online Course</h2>
              <button onClick={() => setShowManualEnrollModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleManualOnlineEnroll}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Select Student *</label>
                <select
                  value={manualEnrollForm.student_id}
                  onChange={(e) => setManualEnrollForm(prev => ({ ...prev, student_id: e.target.value }))}
                  className="form-input"
                  required
                  data-testid="manual-enroll-student-select"
                >
                  <option value="">-- Select a Student --</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.full_name} ({student.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Select Online Course *</label>
                <select
                  value={manualEnrollForm.course_id}
                  onChange={(e) => setManualEnrollForm(prev => ({ ...prev, course_id: e.target.value }))}
                  className="form-input"
                  required
                  data-testid="manual-enroll-course-select"
                >
                  <option value="">-- Select a Course --</option>
                  {availableOnlineCourses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.title} (${course.price})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={manualEnrollForm.waive_payment}
                    onChange={(e) => setManualEnrollForm(prev => ({ ...prev, waive_payment: e.target.checked }))}
                    style={{ width: '18px', height: '18px' }}
                    data-testid="manual-enroll-waive-checkbox"
                  />
                  <span style={{ fontWeight: '600', color: '#059669' }}>Waive Payment (Free Enrollment)</span>
                </label>
                <p style={{ margin: '4px 0 0 26px', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  Check this box to enroll the student without charging any fees (e.g., for grants, scholarships, or promotions)
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowManualEnrollModal(false)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn"
                  style={{ flex: 1, background: '#059669', color: 'white' }}
                  data-testid="manual-enroll-submit-btn"
                >
                  Enroll Student
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {progressUserId && (
        <StudentProgressPanel
          userId={progressUserId}
          onClose={() => setProgressUserId(null)}
          onWaive={fetchData}
        />
      )}

      {accountModal && (
        <div
          className="modal-overlay"
          data-testid="account-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              maxWidth: 480,
              width: '100%',
              padding: 24,
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>
                  {accountModal.mode === 'reset' && 'Reset student password'}
                  {accountModal.mode === 'suspend' && 'Suspend or block student'}
                  {accountModal.mode === 'reinstate' && 'Reinstate student'}
                </h2>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                  {accountModal.student.full_name} · {accountModal.student.email}
                </p>
              </div>
              <button
                onClick={closeAccountModal}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}
                data-testid="account-modal-close"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {accountModal.mode === 'reset' && (
              <div>
                <p style={{ fontSize: '0.875rem', color: '#475569', marginTop: 0 }}>
                  Enter a new password for this student. They will use it to sign in immediately.
                </p>
                <div className="form-group">
                  <label className="form-label">New password</label>
                  <input
                    type="text"
                    autoComplete="new-password"
                    value={accountForm.new_password}
                    onChange={(e) => setAccountForm({ ...accountForm, new_password: e.target.value })}
                    className="form-input"
                    placeholder="At least 6 characters"
                    data-testid="account-modal-new-password"
                  />
                </div>
              </div>
            )}

            {accountModal.mode === 'suspend' && (
              <div>
                <p style={{ fontSize: '0.875rem', color: '#475569', marginTop: 0 }}>
                  Suspending a student blocks them from logging in until reinstated. The reason is
                  shown to the student on the login screen.
                </p>
                <div className="form-group">
                  <label className="form-label">Action</label>
                  <select
                    className="form-input"
                    value={accountForm.status}
                    onChange={(e) => setAccountForm({ ...accountForm, status: e.target.value })}
                    data-testid="account-modal-status"
                  >
                    <option value="suspended">Suspend (temporary)</option>
                    <option value="blocked">Block (severe / indefinite)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Reason for action</label>
                  <textarea
                    rows={3}
                    value={accountForm.reason}
                    onChange={(e) => setAccountForm({ ...accountForm, reason: e.target.value })}
                    className="form-input"
                    placeholder="e.g. Violation of apprenticeship attendance policy on Feb 14, 2026"
                    data-testid="account-modal-reason"
                  />
                </div>
              </div>
            )}

            {accountModal.mode === 'reinstate' && (
              <div>
                <p style={{ fontSize: '0.875rem', color: '#475569', marginTop: 0 }}>
                  Reinstating this student restores their login access immediately.
                </p>
                {accountModal.student.suspension_reason && (
                  <p style={{ fontSize: '0.8125rem', background: '#fef3c7', padding: 10, borderRadius: 6, color: '#92400e' }}>
                    <strong>Original reason:</strong> {accountModal.student.suspension_reason}
                  </p>
                )}
                <div className="form-group">
                  <label className="form-label">Internal note (optional)</label>
                  <textarea
                    rows={3}
                    value={accountForm.note}
                    onChange={(e) => setAccountForm({ ...accountForm, note: e.target.value })}
                    className="form-input"
                    placeholder="Why are you reinstating this account?"
                    data-testid="account-modal-note"
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button
                type="button"
                onClick={closeAccountModal}
                className="btn"
                style={{ flex: 1, background: '#e2e8f0', color: '#0f172a' }}
                data-testid="account-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitAccountAction}
                className="btn"
                style={{
                  flex: 1,
                  background:
                    accountModal.mode === 'suspend'
                      ? '#b91c1c'
                      : accountModal.mode === 'reinstate'
                      ? '#16a34a'
                      : '#0ea5e9',
                  color: 'white',
                  fontWeight: 600,
                }}
                data-testid="account-modal-submit"
              >
                {accountModal.mode === 'reset' && 'Set new password'}
                {accountModal.mode === 'suspend' && (accountForm.status === 'blocked' ? 'Block student' : 'Suspend student')}
                {accountModal.mode === 'reinstate' && 'Reinstate student'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
